// 🔌 Servicio API centralizado - SEGURIDAD MEJORADA
// ✅ Debug condicional (no expone datos en producción)
// ✅ Validación de entrada
// ✅ Mensajes de error genéricos
// ✅ Export con nombre (ESLint compliance)

//desarrollo / produccion configurable
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// 🔐 Variable para habilitar/deshabilitar logs de debug
const DEBUG = process.env.REACT_APP_DEBUG === "true";

const toArray = (val) => (Array.isArray(val) ? val : []);

const log = (message, data) => {
  if (DEBUG) {
    console.log(message, data);
  }
};

const logError = (message, error) => {
  console.error(message, error);
};

// ✅ Validación de entrada
const validateId = (id, fieldName = 'ID') => {
  if (!id || isNaN(id) || parseInt(id) <= 0) {
    throw new Error(`${fieldName} inválido`);
  }
};

const validatePageParams = (page, limit) => {
  const pageNum = parseInt(page);
  const limitNum = parseInt(limit);

  if (isNaN(pageNum) || pageNum < 0 || isNaN(limitNum) || limitNum <= 0) {
    throw new Error('Parámetros de paginación inválidos');
  }
};

const validateString = (value, fieldName, minLength = 1, maxLength = 255) => {
  if (!value || typeof value !== 'string') {
    throw new Error(`${fieldName} requerido`);
  }
  if (value.trim().length < minLength || value.trim().length > maxLength) {
    throw new Error(`${fieldName} inválido`);
  }
};

const validateNumber = (value, fieldName, min = 0, max = null) => {
  const num = parseFloat(value);
  if (isNaN(num) || num < min) {
    throw new Error(`${fieldName} inválido`);
  }
  if (max !== null && num > max) {
    throw new Error(`${fieldName} excede el máximo permitido`);
  }
  return num;
};

class ApiService {

  // ✅ Obtener usuario actual del sessionStorage
  getCurrentUser() {
    try {
      const user = sessionStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      logError('❌ [getCurrentUser] Error parseando usuario:', error.message);
      return null;
    }
  }

  // ✅ Obtener headers con autenticación DINÁMICAMENTE
  getHeaders() {
    const token = sessionStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      log('🔑 [ApiService] Token incluido en headers');
    } else {
      log('⚠️ [ApiService] Sin token en sessionStorage');
    }

    return headers;
  }

  // ✅ Método genérico para requests CON SEGURIDAD MEJORADA
  async request(endpoint, options = {}, extraHeaders = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...extraHeaders,  // ← permite inyectar headers adicionales por llamada
      },
    };

    try {
      log('📡 [request] Iniciando:', { method: config.method || 'GET', endpoint });

      const response = await fetch(url, config);

      if (response.status === 401) {
        log('⚠️ [request] Token expirado (401)');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('authTokenExpired'));
        throw new Error('Sesión expirada');
      }

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: `Error ${response.status}` };
        }

        logError('❌ [request] Error del servidor:', JSON.stringify(errorData));

        // ✅ CREAR UN OBJETO DE ERROR ENRIQUECIDO
        const enhancedError = new Error();
        enhancedError.status = response.status;
        enhancedError.statusText = response.statusText;
        enhancedError.data = errorData; // Guardar los datos completos del error
        enhancedError.response = {
          status: response.status,
          statusText: response.statusText,
          data: errorData,
          headers: Object.fromEntries(response.headers.entries())
        };

        // Extraer mensaje para el error estándar
        let errorMessage = '';
        if (errorData.fieldErrors && typeof errorData.fieldErrors === 'object') {
          const fieldErrors = Object.entries(errorData.fieldErrors)
            .map(([field, message]) => `${field}: ${message}`)
            .join(' | ');
          errorMessage = fieldErrors;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = typeof errorData.message === 'string'
            ? errorData.message
            : JSON.stringify(errorData.message);
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string'
            ? errorData.error
            : JSON.stringify(errorData.error);
        } else {
          errorMessage = DEBUG ? JSON.stringify(errorData) : 'Error en la solicitud';
        }

        enhancedError.message = errorMessage;
        logError('❌ [request] Mensaje:', errorMessage);

        throw enhancedError; // Lanzar el error enriquecido
      }

      const contentType = response.headers.get('content-type');
      const hasBody = contentType && contentType.includes('application/json');
      const data = hasBody ? await response.json() : null;
      log('✅ [request] Exitoso');
      return data;

    } catch (error) {
      logError('🔴 [request] Error:', error.message);
      throw error;
    }
  }

  // ========== 🔐 AUTENTICACIÓN ==========
  async login(username, password) {
    try {
      validateString(username, 'Username', 3, 50);
      validateString(password, 'Password', 8, 128);

      const data = await this.request('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });

      if (data.token) {
        this.setToken(data.token);
      }

      return data;
    } catch (error) {
      logError('❌ [login] Error:', error.message);
      throw error;
    }
  }

  async register(userData) {
    try {
      if (!userData || typeof userData !== 'object') {
        throw new Error('Datos de registro inválidos');
      }

      return this.request('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
    } catch (error) {
      logError('❌ [register] Error:', error.message);
      throw error;
    }
  }

  // ✅ Guardar token en sessionStorage
  setToken(token) {
    if (!token || typeof token !== 'string') {
      throw new Error('Token inválido');
    }
    sessionStorage.setItem('token', token);
    log('✅ [setToken] Token guardado');
  }

  // ✅ Logout - limpiar sessionStorage
  logout() {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    log('👋 [logout] Sesión cerrada');
  }



  // ========== 👥 MIEMBROS ==========
  async getMembers(page = 0, limit = 10) {
    try {
      validatePageParams(page, limit);
      return this.request(`/member?page=${page}&limit=${limit}`);
    } catch (error) {
      logError('❌ [getMembers] Error:', error.message);
      throw error;
    }
  }

  async getMemberById(id) {
    try {
      validateId(id, 'memberId');
      return this.request(`/member/find/${id}`);
    } catch (error) {
      logError('❌ [getMemberById] Error:', error.message);
      throw error;
    }
  }

  async getAllMembers() {
    try {
      const response = await this.request('/member?page=0&limit=500');
      return response.content || response;
    } catch (error) {
      logError('❌ [getAllMembers] Error:', error.message);
      throw error;
    }
  }

  async createMember(memberData) {
    try {
      if (!memberData || typeof memberData !== 'object') {
        throw new Error('Datos de miembro inválidos');
      }
      return this.request('/member/save', {
        method: 'POST',
        body: JSON.stringify(memberData),
      });
    } catch (error) {
      logError('❌ [createMember] Error:', error.message);
      throw error;
    }
  }

  async updateMember(id, memberData) {
    try {
      validateId(id, 'memberId');
      if (!memberData || typeof memberData !== 'object') {
        throw new Error('Datos de miembro inválidos');
      }
      return this.request(`/member/patch/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(memberData),
      });
    } catch (error) {
      logError('❌ [updateMember] Error:', error.message);
      throw error;
    }
  }

  async deleteMember(id) {
    try {
      validateId(id, 'memberId');
      return this.request(`/member/delete/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      logError('❌ [deleteMember] Error:', error.message);
      throw error;
    }
  }

  async enrollMemberInNextLevel(id) {
    try {
      validateId(id, 'memberId');
      return this.request(`/member/enroll-next-level/${id}`, {
        method: 'POST',
      });
    } catch (error) {
      logError('❌ [enrollMemberInNextLevel] Error:', error.message);
      throw error;
    }
  }

  async getMemberEnrollmentHistory(id) {
    try {
      validateId(id, 'memberId');
      return this.request(`/member/enrollment-history/${id}`);
    } catch (error) {
      logError('❌ [getMemberEnrollmentHistory] Error:', error.message);
      throw error;
    }
  }

  // ========== 📋 COHORTES (ENROLLMENTS) ==========

  async getEnrollments() {
    try {
      return this.request('/enrollment/cohorts/findAll');
    } catch (error) {
      logError('❌ [getEnrollments] Error:', error.message);
      throw error;
    }
  }

  async getEnrollmentsCard() {
    try {
      return this.request('/enrollment');
    } catch (error) {
      logError('❌ [getEnrollmentsCard] Error:', error.message);
      throw error;
    }
  }

  async getEnrollmentsPaginated(page = 0, limit = 10) {
    try {
      validatePageParams(page, limit);
      return this.request(`/enrollment?page=${page}&limit=${limit}`);
    } catch (error) {
      logError('❌ [getEnrollmentsPaginated] Error:', error.message);
      throw error;
    }
  }

  async getEnrollmentById(id) {
    try {
      validateId(id, 'enrollmentId');
      return this.request(`/enrollment/cohorts/find/${id}`);
    } catch (error) {
      logError('❌ [getEnrollmentById] Error:', error.message);
      throw error;
    }
  }

  async getAvailableCohortsByLevel(level) {
    try {
      validateString(level, 'Level', 1, 50);
      return this.request(`/enrollment/available-cohorts/${level}`);
    } catch (error) {
      logError('❌ [getAvailableCohortsByLevel] Error:', error.message);
      throw error;
    }
  }

  async createEnrollment(enrollmentData) {
    try {
      if (!enrollmentData || typeof enrollmentData !== 'object') {
        throw new Error('Datos de cohorte inválidos');
      }
      return this.request('/enrollment/create-cohort', {
        method: 'POST',
        body: JSON.stringify(enrollmentData),
      });
    } catch (error) {
      logError('❌ [createEnrollment] Error:', error.message);
      throw error;
    }
  }

  async updateEnrollmentStatus(id, status) {
    try {
      validateId(id, 'enrollmentId');
      validateString(status, 'Status', 1, 50);
      return this.request(`/enrollment/cohort/${id}/status?status=${encodeURIComponent(status)}`, {
        method: 'PUT',
      });
    } catch (error) {
      logError('❌ [updateEnrollmentStatus] Error:', error.message);
      throw error;
    }
  }

  async editEnrollment(enrollmentId, updateData) {
    try {
      validateId(enrollmentId, 'enrollmentId');
      if (!updateData || typeof updateData !== 'object') {
        throw new Error('Datos de actualización inválidos');
      }

      log('📝 [editEnrollment] Editando cohorte ID:', enrollmentId);

      const response = await this.request(`/enrollment/cohorts/${enrollmentId}/edit`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      log('✅ [editEnrollment] Éxito');
      return response;
    } catch (error) {
      logError('❌ [editEnrollment] Error:', error.message);
      throw error;
    }
  }

  async editEnrollmentWithStatus(enrollmentId, updateData, newStatus) {
    try {
      validateId(enrollmentId, 'enrollmentId');
      if (!updateData || typeof updateData !== 'object') {
        throw new Error('Datos de actualización inválidos');
      }
      if (newStatus && typeof newStatus !== 'string') {
        throw new Error('Status inválido');
      }

      log('📝 [editEnrollmentWithStatus] Editando cohorte ID:', enrollmentId);

      const params = newStatus ? `?newStatus=${encodeURIComponent(newStatus)}` : '';

      const response = await this.request(`/enrollment/cohorts/${enrollmentId}/edit-with-status${params}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      if (newStatus === 'CANCELLED') {
        console.warn('🚫 ATENCIÓN: Se cancelaron estudiantes inscritos en esta cohorte');
      }

      return response;
    } catch (error) {
      logError('❌ [editEnrollmentWithStatus] Error:', error.message);
      throw error;
    }
  }

  /**
   * Recupera una cohorte cancelada incorrectamente por el job automático.
   * POST /api/v1/enrollments/{id}/recover
   * * @param {number} id - ID de la cohorte a recuperar
   * @returns {Object} Datos de la cohorte recuperada (cohortId, newStatus, etc.)
   */
  async recoverCancelledCohort(id) {
    try {
      validateId(id, 'enrollmentId');
      
      log('🔧 [recoverCancelledCohort] Intentando recuperar cohorte ID:', id);

      // Nota: Según el Java docstring el endpoint es /enrollments/{id}/recover (plural).
      // Si tu backend usa el singular o un prefijo como en tus otros métodos 
      // (ej. /enrollment/cohorts/{id}/recover), ajusta la URL aquí abajo.
      const response = await this.request(`/enrollment/${id}/recover`, {
        method: 'POST',
      });

      log('✅ [recoverCancelledCohort] Éxito - Nuevo estado:', response?.newStatus);
      return response;
      
    } catch (error) {
      logError('❌ [recoverCancelledCohort] Error:', error.message);
      throw error;
    }
  }

  /**
   * Matricula automáticamente a estudiantes aprobados al siguiente nivel,
   * solo si el siguiente nivel NO requiere pago.
   * POST /api/v1/enrollment/cohorts/{cohortId}/auto-enroll-next
   * @param {number} cohortId - ID de la cohorte
   * @returns {Object} Resumen de matrículas (enrolled, alreadyEnrolled, noCohortAvailable, errors, requiresPayment)
   */
  async autoEnrollNext(cohortId) {
    try {
      validateId(cohortId, 'cohortId');

      log('🚀 [autoEnrollNext] Ejecutando auto-matrícula para cohorte:', cohortId);

      const response = await this.request(`/enrollment/cohorts/${cohortId}/auto-enroll-next`, {
        method: 'POST',
      });

      log('✅ [autoEnrollNext] Resultado:', response);
      return response;
    } catch (error) {
      logError('❌ [autoEnrollNext] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtiene los estudiantes aprobados de una cohorte.
   * GET /api/v1/enrollment/cohorts/{cohortId}/approved-students
   * @param {number} cohortId - ID de la cohorte
   * @returns {Array} Lista de estudiantes aprobados
   */
  async getApprovedStudents(cohortId) {
    try {
      validateId(cohortId, 'cohortId');

      log('📋 [getApprovedStudents] Obteniendo aprobados de cohorte:', cohortId);

      const response = await this.request(`/enrollment/cohorts/${cohortId}/approved-students`);

      log('✅ [getApprovedStudents] Éxito -', response?.length || 0, 'estudiantes');
      return response;
    } catch (error) {
      logError('❌ [getApprovedStudents] Error:', error.message);
      throw error;
    }
  }


  // ========== 📚 NIVELES FORMATIVOS ==========

  async getActiveLevels() {
    try {
      log('📚 [getActiveLevels] Obteniendo niveles activos');
      const response = await this.request('/levels');
      log('✅ [getActiveLevels] Éxito -', response?.length || 0, 'niveles');
      return response;
    } catch (error) {
      logError('❌ [getActiveLevels] Error:', error.message);
      return this.getDefaultLevels();
    }
  }

  async getAllLevels() {
    try {
      log('📚 [getAllLevels] Obteniendo todos los niveles');
      const response = await this.request('/levels/all');
      log('✅ [getAllLevels] Éxito -', response?.length || 0, 'niveles');
      return response;
    } catch (error) {
      logError('❌ [getAllLevels] Error:', error.message);
      return this.getDefaultLevels();
    }
  }

  async getLevelByCode(code) {
    try {
      validateString(code, 'code', 1, 50);
      log('🔍 [getLevelByCode] Buscando nivel:', code);
      const response = await this.request(`/levels/code/${code}`);
      return response;
    } catch (error) {
      logError('❌ [getLevelByCode] Error:', error.message);
      throw error;
    }
  }

  getDefaultLevels() {
    return [
      { id: 1, code: 'PREENCUENTRO', displayName: 'Pre-encuentro', levelOrder: 1, isActive: true },
      { id: 2, code: 'ENCUENTRO', displayName: 'Encuentro', levelOrder: 2, isActive: true },
      { id: 3, code: 'POST_ENCUENTRO', displayName: 'Post-encuentro', levelOrder: 3, isActive: true },
      { id: 4, code: 'BAUTIZOS', displayName: 'Bautizos', levelOrder: 4, isActive: true },
      { id: 5, code: 'ESENCIA_1', displayName: 'ESENCIA 1', levelOrder: 5, isActive: true },
      { id: 6, code: 'ESENCIA_2', displayName: 'ESENCIA 2', levelOrder: 6, isActive: true },
      { id: 7, code: 'ESENCIA_3', displayName: 'ESENCIA 3', levelOrder: 7, isActive: true },
      { id: 8, code: 'SANIDAD_INTEGRAL_RAICES', displayName: 'Sanidad Integral Raíces', levelOrder: 8, isActive: true },
      { id: 9, code: 'ESENCIA_4', displayName: 'ESENCIA 4', levelOrder: 9, isActive: true },
      { id: 10, code: 'ADIESTRAMIENTO', displayName: 'Adiestramiento', levelOrder: 10, isActive: true },
      { id: 11, code: 'GRADUACION', displayName: 'Graduación', levelOrder: 11, isActive: true }
    ];
  }

 


  // ========== 🎓 INSCRIPCIONES DE ESTUDIANTES ==========

  async getStudentEnrollments(page = 0, limit = 10) {
    try {
      validatePageParams(page, limit);
      return this.request(`/student-enrollment?page=${page}&limit=${limit}`);
    } catch (error) {
      logError('❌ [getStudentEnrollments] Error:', error.message);
      throw error;
    }
  }

  async getStudentEnrollmentById(id) {
    try {
      validateId(id, 'studentEnrollmentId');
      return this.request(`/student-enrollment/${id}`);
    } catch (error) {
      logError('❌ [getStudentEnrollmentById] Error:', error.message);
      throw error;
    }
  }

  async getStudentEnrollmentsByEnrollment(enrollmentId) {
    try {
      validateId(enrollmentId, 'enrollmentId');

      log('📡 [getStudentEnrollmentsByEnrollment] Obteniendo estudiantes de cohorte:', enrollmentId);

      try {
        const response = await this.request(`/student-enrollment/by-cohort/${enrollmentId}`);
        log('✅ [getStudentEnrollmentsByEnrollment] Exitoso');
        return response;
      } catch (error1) {
        log('⚠️ [getStudentEnrollmentsByEnrollment] Intento 1 falló, intentando alternativa...');

        try {
          const enrollment = await this.request(`/enrollment/${enrollmentId}`);
          const students = enrollment?.studentEnrollments || [];
          log('✅ [getStudentEnrollmentsByEnrollment] Exitoso (alternativa)');
          return students;
        } catch (error2) {
          log('⚠️ [getStudentEnrollmentsByEnrollment] Intento 2 falló, intentando intento 3...');

          try {
            const allStudentEnrollments = await this.request('/student-enrollment');
            const filtered = allStudentEnrollments?.filter(se => se.enrollmentId === enrollmentId) || [];
            log('✅ [getStudentEnrollmentsByEnrollment] Exitoso (alternativa 2)');
            return filtered;
          } catch (error3) {
            logError('❌ [getStudentEnrollmentsByEnrollment] Todos los intentos fallaron');
            return [];
          }
        }
      }
    } catch (error) {
      logError('❌ [getStudentEnrollmentsByEnrollment] Error de validación:', error.message);
      throw error;
    }
  }

  async getStudentEnrollmentsByMember(memberId) {
    try {
      validateId(memberId, 'memberId');
      return this.request(`/student-enrollment/by-member/${memberId}`);
    } catch (error) {
      logError('❌ [getStudentEnrollmentsByMember] Error:', error.message);
      throw error;
    }
  }

  async createStudentEnrollment(memberId, enrollmentId) {
    try {
      validateId(memberId, 'memberId');
      validateId(enrollmentId, 'enrollmentId');
      return this.request(`/student-enrollment?memberId=${memberId}&enrollmentId=${enrollmentId}`, {
        method: 'POST',
      });
    } catch (error) {
      logError('❌ [createStudentEnrollment] Error:', error.message);
      throw error;
    }
  }

  async updateStudentEnrollment(id, updateData) {
    try {
      validateId(id, 'studentEnrollmentId');
      if (!updateData || typeof updateData !== 'object') {
        throw new Error('Datos de actualización inválidos');
      }

      let url = `/student-enrollment/${id}?`;
      const params = [];

      if (updateData.status) params.push(`status=${encodeURIComponent(updateData.status)}`);
      if (updateData.finalAttendancePercentage !== undefined) {
        validateNumber(updateData.finalAttendancePercentage, 'finalAttendancePercentage', 0, 100);
        params.push(`finalAttendancePercentage=${updateData.finalAttendancePercentage}`);
      }
      if (updateData.passed !== undefined) params.push(`passed=${updateData.passed}`);

      url += params.join('&');

      return this.request(url, {
        method: 'PUT',
      });
    } catch (error) {
      logError('❌ [updateStudentEnrollment] Error:', error.message);
      throw error;
    }
  }

  async withdrawStudentFromCohort(id) {
    try {
      validateId(id, 'studentEnrollmentId');
      return this.request(`/student-enrollment/${id}/withdraw`, {
        method: 'POST',
      });
    } catch (error) {
      logError('❌ [withdrawStudentFromCohort] Error:', error.message);
      throw error;
    }
  }

  async deleteStudentEnrollment(id) {
    try {
      validateId(id, 'studentEnrollmentId');
      return this.request(`/student-enrollment/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      logError('❌ [deleteStudentEnrollment] Error:', error.message);
      throw error;
    }
  }

  async getStudentDetailedReport(id) {
    try {
      validateId(id, 'studentEnrollmentId');
      return this.request(`/student-enrollment/${id}/detailed-report`);
    } catch (error) {
      logError('❌ [getStudentDetailedReport] Error:', error.message);
      throw error;
    }
  }

  // ========== NUEVO MÉTODO PARA REPORTE POR NIVEL ==========

  async getLevelStudents(level, status = null) {
    try {
      validateString(level, 'level', 1, 50);

      // Construir URL - EL ENDPOINT CORRECTO ES /by-level/{level}
      let url = `/student-enrollment/by-level/${level}`;
      if (status) {
        url += `?status=${status}`;
      }

      log(`📊 [getLevelStudents] Obteniendo estudiantes del nivel: ${level}`);
      const response = await this.request(url);

      // La respuesta es un StudentsByLevelReportDTO.LevelDetail
      return response;

    } catch (error) {
      logError('❌ [getLevelStudents] Error:', error.message);
      throw error;
    }
  }

  // ========== NUEVO MÉTODO PARA REPORTE JERÁRQUICO COHORTE ==========

  async getCohortHierarchyReport(cohortId) {
    try {
      validateId(cohortId, 'cohortId');
      log(`📊 [getCohortHierarchyReport] Obteniendo reporte jerárquico G12 para cohorte: ${cohortId}`);
      const response = await this.request(`/student-enrollment/cohorts/${cohortId}/hierarchy-report`);
      return response;
    } catch (error) {
      logError('❌ [getCohortHierarchyReport] Error:', error.message);
      throw error;
    }
  }

  // ========== 📖 LECCIONES ==========

  async getLessons(page = 0, limit = 10) {
    try {
      validatePageParams(page, limit);
      return this.request(`/lesson?page=${page}&limit=${limit}`);
    } catch (error) {
      logError('❌ [getLessons] Error:', error.message);
      throw error;
    }
  }

  async getLessonById(id) {
    try {
      validateId(id, 'lessonId');
      return this.request(`/lesson/${id}`);
    } catch (error) {
      logError('❌ [getLessonById] Error:', error.message);
      throw error;
    }
  }

  async getLessonsByEnrollment(enrollmentId) {
    try {
      validateId(enrollmentId, 'enrollmentId');
      return this.request(`/lesson/enrollment/${enrollmentId}`);
    } catch (error) {
      logError('❌ [getLessonsByEnrollment] Error:', error.message);
      throw error;
    }
  }

  async createLesson(lessonData) {
    try {
      if (!lessonData || typeof lessonData !== 'object') {
        throw new Error('Datos de lección inválidos');
      }
      return this.request('/lesson/create', {
        method: 'POST',
        body: JSON.stringify(lessonData),
      });
    } catch (error) {
      logError('❌ [createLesson] Error:', error.message);
      throw error;
    }
  }

  async createDefaultLessonPlan(enrollmentId) {
    try {
      validateId(enrollmentId, 'enrollmentId');
      return this.request(`/lesson/create-plan/${enrollmentId}`, {
        method: 'POST',
      });
    } catch (error) {
      logError('❌ [createDefaultLessonPlan] Error:', error.message);
      throw error;
    }
  }

  async updateLesson(id, lessonData) {
    try {
      validateId(id, 'lessonId');
      if (!lessonData || typeof lessonData !== 'object') {
        throw new Error('Datos de lección inválidos');
      }
      return this.request(`/lesson/${id}`, {
        method: 'PUT',
        body: JSON.stringify(lessonData),
      });
    } catch (error) {
      logError('❌ [updateLesson] Error:', error.message);
      throw error;
    }
  }

  async deleteLesson(id) {
    try {
      validateId(id, 'lessonId');
      return this.request(`/lesson/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      logError('❌ [deleteLesson] Error:', error.message);
      throw error;
    }
  }

  // ========== ✅ ASISTENCIAS ==========

  async getAttendanceByLesson(lessonId) {
    try {
      validateId(lessonId, 'lessonId');
      log('🔍 [getAttendanceByLesson] Consultando lección:', lessonId);
      // IMPORTANTE: Asegúrate de que este sea el endpoint correcto en tu backend
      return await this.request(`/attendance/lesson/${lessonId}`);
    } catch (error) {
      logError('❌ [getAttendanceByLesson] Error:', error.message);
      throw error;
    }
  }
  async recordAttendance(attendanceData) {
    try {
      if (!attendanceData || typeof attendanceData !== 'object') {
        throw new Error('Datos de asistencia inválidos');
      }

      validateId(attendanceData.studentEnrollmentId, 'studentEnrollmentId');
      validateId(attendanceData.lessonId, 'lessonId');
      validateString(attendanceData.recordedBy, 'recordedBy', 1, 100);
      validateString(attendanceData.score, 'score', 1, 50);

      if (attendanceData.present === undefined || attendanceData.present === null) {
        throw new Error('present requerido');
      }

      const bodyData = {
        studentEnrollmentId: Number(attendanceData.studentEnrollmentId),
        lessonId: Number(attendanceData.lessonId),
        present: attendanceData.present === true,
        recordedBy: String(attendanceData.recordedBy),
        score: String(attendanceData.score)
      };

      log('📤 [recordAttendance] Enviando asistencia');

      const response = await this.request('/attendance/record', {
        method: 'POST',
        body: JSON.stringify(bodyData)
      });

      log('✅ [recordAttendance] Éxito');
      return response;

    } catch (error) {
      logError('❌ [recordAttendance] Error:', error.message);
      throw error;
    }
  }

  async getAttendancesByLesson(lessonId) {
    try {
      validateId(lessonId, 'lessonId');
      return this.request(`/attendance/lesson/${lessonId}`);
    } catch (error) {
      logError('❌ [getAttendancesByLesson] Error:', error.message);
      throw error;
    }
  }

  async initializeLessonAttendance(lessonId) {
    try {
      validateId(lessonId, 'lessonId');
      return this.request(`/attendance/lesson/${lessonId}/initialize`, {
        method: 'POST',
      });
    } catch (error) {
      logError('❌ [initializeLessonAttendance] Error:', error.message);
      throw error;
    }
  }

  async getStudentAttendanceReport(studentId) {
    try {
      validateId(studentId, 'studentId');
      return this.request(`/attendance/student/${studentId}/report`);
    } catch (error) {
      logError('❌ [getStudentAttendanceReport] Error:', error.message);
      throw error;
    }
  }

  async updateAttendance(id, attendanceData) {
    try {
      validateId(id, 'attendanceId');
      if (!attendanceData || typeof attendanceData !== 'object') {
        throw new Error('Datos de asistencia inválidos');
      }
      return this.request(`/attendance/${id}`, {
        method: 'PUT',
        body: JSON.stringify(attendanceData),
      });
    } catch (error) {
      logError('❌ [updateAttendance] Error:', error.message);
      throw error;
    }
  }

  // ========== 👤 USUARIOS ==========

  async getUsers() {
    try {
      return this.request('/auth/users');
    } catch (error) {
      logError('❌ [getUsers] Error:', error.message);
      throw error;
    }
  }

  async updateUser(id, userData) {
    try {
      validateId(id, 'userId');
      if (!userData || typeof userData !== 'object') {
        throw new Error('Datos de usuario inválidos');
      }
      return this.request(`/auth/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });
    } catch (error) {
      logError('❌ [updateUser] Error:', error.message);
      throw error;
    }
  }

  async deleteUser(id) {
    try {
      validateId(id, 'userId');
      return this.request(`/auth/users/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      logError('❌ [deleteUser] Error:', error.message);
      throw error;
    }
  }

  // ========== 💰 FINANZAS ==========

  async getFinances(page = 0, limit = 10) {
    try {
      validatePageParams(page, limit);
      log('📊 [getFinances] Obteniendo finanzas');
      return this.request(`/finances?page=${page}&limit=${limit}`);
    } catch (error) {
      logError('❌ [getFinances] Error:', error.message);
      throw error;
    }
  }

  async getFinanceById(id) {
    try {
      validateId(id, 'financeId');
      return this.request(`/finances/${id}`);
    } catch (error) {
      logError('❌ [getFinanceById] Error:', error.message);
      throw error;
    }
  }

  async createFinance(financeData) {
    try {
      if (!financeData || typeof financeData !== 'object') {
        throw new Error('Datos de finanza inválidos');
      }

      let recordedBy = financeData.recordedBy;
      if (!recordedBy) {
        const currentUser = this.getCurrentUser();
        recordedBy = currentUser?.username || 'Sistema';
        log('📝 [createFinance] recordedBy auto-llenado con:', recordedBy);
      }

      const body = {
        memberId: validateNumber(financeData.memberId, 'memberId'),
        memberName: financeData.memberName,
        amount: validateNumber(financeData.amount, 'amount', 0),
        incomeConcept: financeData.incomeConcept,
        incomeMethod: financeData.incomeMethod,
        description: financeData.description || '',
        recordedBy: recordedBy,
        registrationDate: financeData.registrationDate,
        isVerified: financeData.isVerified || false,
      };

      const idempotencyKey = `finance-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      log('📤 [createFinance] Enviando con idempotencyKey:', idempotencyKey);

      const response = await this.request(
        '/finances',
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
        {
          'X-Idempotency-Key': idempotencyKey,
        }
      );

      log('✅ [createFinance] Éxito - ID:', response?.id);
      return response;

    } catch (error) {
      logError('❌ [createFinance] Error:', error.message);
      throw error;
    }
  }


  async updateFinance(id, financeData) {
    try {
      validateId(id, 'financeId');
      if (!financeData || typeof financeData !== 'object') {
        throw new Error('Datos de finanza inválidos');
      }

      let recordedBy = financeData.recordedBy;
      if (!recordedBy) {
        const currentUser = this.getCurrentUser();
        recordedBy = currentUser?.username || 'Sistema';
        log('📝 [updateFinance] recordedBy auto-llenado con:', recordedBy);
      }

      const body = {
        memberId: validateNumber(financeData.memberId, 'memberId'),
        memberName: financeData.memberName,
        amount: validateNumber(financeData.amount, 'amount', 0),
        incomeConcept: financeData.incomeConcept,
        incomeMethod: financeData.incomeMethod,
        description: financeData.description || '',
        recordedBy: recordedBy,
        registrationDate: financeData.registrationDate,
        isVerified: financeData.isVerified || false,
      };

      log('📝 [updateFinance] Actualizando finanza ID:', id);

      const response = await this.request(`/finances/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });

      log('✅ [updateFinance] Éxito');
      return response;
    } catch (error) {
      logError('❌ [updateFinance] Error:', error.message);
      throw error;
    }
  }

  async deleteFinance(id) {
    try {
      validateId(id, 'financeId');
      const response = await this.request(`/finances/${id}`, {
        method: 'DELETE',
      });
      log('✅ [deleteFinance] Éxito');
      return response;
    } catch (error) {
      logError('❌ [deleteFinance] Error:', error.message);
      throw error;
    }
  }

  async getFinancesByMember(memberId, page = 0, limit = 10) {
    try {
      validateId(memberId, 'memberId');
      validatePageParams(page, limit);
      return this.request(`/finances/member/${memberId}?page=${page}&limit=${limit}`);
    } catch (error) {
      logError('❌ [getFinancesByMember] Error:', error.message);
      throw error;
    }
  }

  async getTotalFinancesByMember(memberId) {
    try {
      validateId(memberId, 'memberId');
      return this.request(`/finances/member/${memberId}/total`);
    } catch (error) {
      logError('❌ [getTotalFinancesByMember] Error:', error.message);
      throw error;
    }
  }

  async getFinancesByDateRange(startDate, endDate) {
    try {
      validateString(startDate, 'startDate', 1, 20);
      validateString(endDate, 'endDate', 1, 20);
      return this.request(`/finances/date-range?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`);
    } catch (error) {
      logError('❌ [getFinancesByDateRange] Error:', error.message);
      throw error;
    }
  }

  async getFinancesByMonth(year, month) {
    try {
      validateNumber(year, 'year', 1900);
      validateNumber(month, 'month', 1, 12);
      return this.request(`/finances/month/${year}/${month}`);
    } catch (error) {
      logError('❌ [getFinancesByMonth] Error:', error.message);
      throw error;
    }
  }

  async getFinancesByYear(year) {
    try {
      validateNumber(year, 'year', 1900);
      return this.request(`/finances/year/${year}`);
    } catch (error) {
      logError('❌ [getFinancesByYear] Error:', error.message);
      throw error;
    }
  }

  async getFinancesByConcept(concept) {
    try {
      validateString(concept, 'concept', 1, 100);
      return this.request(`/finances/concept/${encodeURIComponent(concept)}`);
    } catch (error) {
      logError('❌ [getFinancesByConcept] Error:', error.message);
      throw error;
    }
  }

  async getFinancesByMethod(method) {
    try {
      validateString(method, 'method', 1, 100);
      return this.request(`/finances/method/${encodeURIComponent(method)}`);
    } catch (error) {
      logError('❌ [getFinancesByMethod] Error:', error.message);
      throw error;
    }
  }

  async getVerifiedFinances() {
    try {
      return this.request('/finances/verified');
    } catch (error) {
      logError('❌ [getVerifiedFinances] Error:', error.message);
      throw error;
    }
  }

  async getUnverifiedFinances() {
    try {
      return this.request('/finances/unverified');
    } catch (error) {
      logError('❌ [getUnverifiedFinances] Error:', error.message);
      throw error;
    }
  }

  async verifyFinance(id) {
    try {
      validateId(id, 'financeId');
      return this.request(`/finances/${id}/verify`, {
        method: 'PATCH',
      });
    } catch (error) {
      logError('❌ [verifyFinance] Error:', error.message);
      throw error;
    }
  }

  // ========== 👥 LÍDERES ==========

  async getLeaders() {
    try {
      log('📋 [getLeaders] Obteniendo todos los líderes');
      const response = await this.request('/leaders');
      log('✅ [getLeaders] Éxito -', response?.length || 0, 'líderes');
      return response;
    } catch (error) {
      logError('❌ [getLeaders] Error:', error.message);
      throw error;
    }
  }

  async getActiveLeaders() {
    try {
      log('✅ [getActiveLeaders] Obteniendo líderes activos');
      const response = await this.request('/leaders/active');
      return response;
    } catch (error) {
      logError('❌ [getActiveLeaders] Error:', error.message);
      throw error;
    }
  }

  async getSuspendedLeaders() {
    try {
      log('⏸️ [getSuspendedLeaders] Obteniendo líderes suspendidos');
      const response = await this.request('/leaders/suspended');
      return response;
    } catch (error) {
      logError('❌ [getSuspendedLeaders] Error:', error.message);
      throw error;
    }
  }

  async getInactiveLeaders() {
    try {
      log('⏹️ [getInactiveLeaders] Obteniendo líderes inactivos');
      const response = await this.request('/leaders/inactive');
      return response;
    } catch (error) {
      logError('❌ [getInactiveLeaders] Error:', error.message);
      throw error;
    }
  }

  async getLeaderById(id) {
    try {
      validateId(id, 'leaderId');
      log('🔍 [getLeaderById] Buscando líder ID:', id);
      const response = await this.request(`/leaders/${id}`);
      return response;
    } catch (error) {
      logError('❌ [getLeaderById] Error:', error.message);
      throw error;
    }
  }

  async getLeaderByMemberId(memberId) {
    try {
      validateId(memberId, 'memberId');
      log('🔍 [getLeaderByMemberId] Buscando líder del miembro ID:', memberId);
      const response = await this.request(`/leaders/member/${memberId}`);
      return response;
    } catch (error) {
      logError('❌ [getLeaderByMemberId] Error:', error.message);
      throw error;
    }
  }

  async getLeadersByType(leaderType) {
    try {
      validateString(leaderType, 'leaderType', 1, 50);
      if (!['SERVANT', 'AUXILIARY_SERVANT', 'LEADER_144', 'LEADER_12'].includes(leaderType)) {
        throw new Error('Tipo de líder inválido');
      }
      log('🔍 [getLeadersByType] Buscando líderes tipo:', leaderType);
      const response = await this.request(`/leaders/type/${leaderType}`);
      return response;
    } catch (error) {
      logError('❌ [getLeadersByType] Error:', error.message);
      throw error;
    }
  }

  async promoteToLeader(memberId, leaderType, cellGroupCode = null, notes = null) {
    try {
      validateId(memberId, 'memberId');
      validateString(leaderType, 'leaderType', 1, 50);
      if (!['SERVANT', 'AUXILIARY_SERVANT', 'LEADER_144', 'LEADER_12'].includes(leaderType)) {
        throw new Error('Tipo de líder inválido. Debe ser SERVANT, AUXILIARY_SERVANT, LEADER_144 o LEADER_12');
      }

      const params = new URLSearchParams();
      params.append('memberId', memberId);
      params.append('leaderType', leaderType);
      if (cellGroupCode) params.append('cellGroupCode', cellGroupCode.trim());
      if (notes) params.append('notes', notes.trim());

      log('🌟 [promoteToLeader] Promoviendo miembro:', { memberId, leaderType });

      const response = await this.request(`/leaders/promote?${params.toString()}`, {
        method: 'POST',
      });

      log('✅ [promoteToLeader] Éxito - Líder ID:', response?.leaderId);
      return response;
    } catch (error) {
      logError('❌ [promoteToLeader] Error:', error.message);
      throw error;
    }
  }

  async checkEligibility(memberId, leaderType) {
    try {
      validateId(memberId, 'memberId');
      validateString(leaderType, 'leaderType', 1, 50);
      if (!['SERVANT', 'AUXILIARY_SERVANT', 'LEADER_144', 'LEADER_12'].includes(leaderType)) {
        throw new Error('Tipo de líder inválido. Debe ser SERVANT, AUXILIARY_SERVANT, LEADER_144 o LEADER_12');
      }

      log('🔍 [checkEligibility] Verificando miembro:', { memberId, leaderType });

      const response = await this.request(`/leaders/eligibility/${memberId}?leaderType=${leaderType}`);

      log('📊 [checkEligibility] Resultado:', {
        isEligible: response?.isEligible,
        passed: response?.passedRequirements?.length || 0,
        failed: response?.failedRequirements?.length || 0
      });

      return response;
    } catch (error) {
      logError('❌ [checkEligibility] Error:', error.message);
      throw error;
    }
  }

  async verifyAllLeaders() {
    try {
      log('🔄 [verifyAllLeaders] Verificando todos los líderes activos');
      const response = await this.request('/leaders/verify-all', { method: 'POST' });
      log('✅ [verifyAllLeaders] Éxito -', { total: response?.totalVerified, suspendidos: response?.suspended, válidos: response?.stillValid });
      return response;
    } catch (error) {
      logError('❌ [verifyAllLeaders] Error:', error.message);
      throw error;
    }
  }

  async verifyLeader(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('🔍 [verifyLeader] Verificando líder ID:', leaderId);
      const response = await this.request(`/leaders/${leaderId}/verify`, { method: 'POST' });
      if (response?.wasSuspended) {
        log('⚠️ [verifyLeader] Líder suspendido automáticamente');
      } else {
        log('✅ [verifyLeader] Líder verificado, cumple requisitos');
      }
      return response;
    } catch (error) {
      logError('❌ [verifyLeader] Error:', error.message);
      throw error;
    }
  }

  async reactivateSuspendedLeaders() {
    try {
      log('▶️ [reactivateSuspendedLeaders] Intentando reactivar suspendidos');
      const response = await this.request('/leaders/reactivate-suspended', { method: 'POST' });
      log('✅ [reactivateSuspendedLeaders] Éxito -', { revisados: response?.totalChecked, reactivados: response?.reactivated, aúnSuspendidos: response?.stillSuspended });
      return response;
    } catch (error) {
      logError('❌ [reactivateSuspendedLeaders] Error:', error.message);
      throw error;
    }
  }

  async suspendLeader(leaderId, reason) {
    try {
      validateId(leaderId, 'leaderId');
      validateString(reason, 'reason', 3, 500);
      log('⏸️ [suspendLeader] Suspendiendo líder ID:', leaderId);
      const params = new URLSearchParams();
      params.append('reason', reason.trim());
      const response = await this.request(`/leaders/${leaderId}/suspend?${params.toString()}`, { method: 'PUT' });
      log('✅ [suspendLeader] Líder suspendido');
      return response;
    } catch (error) {
      logError('❌ [suspendLeader] Error:', error.message);
      throw error;
    }
  }

  async unsuspendLeader(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('▶️ [unsuspendLeader] Reactivando líder suspendido ID:', leaderId);
      const response = await this.request(`/leaders/${leaderId}/unsuspend`, { method: 'PUT' });
      log('✅ [unsuspendLeader] Líder reactivado');
      return response;
    } catch (error) {
      logError('❌ [unsuspendLeader] Error:', error.message);
      throw error;
    }
  }

  async deactivateLeader(leaderId, reason) {
    try {
      validateId(leaderId, 'leaderId');
      validateString(reason, 'reason', 3, 500);
      log('⏹️ [deactivateLeader] Desactivando líder ID:', leaderId);
      const params = new URLSearchParams();
      params.append('reason', reason.trim());
      const response = await this.request(`/leaders/${leaderId}/deactivate?${params.toString()}`, { method: 'PUT' });
      log('✅ [deactivateLeader] Líder desactivado');
      return response;
    } catch (error) {
      logError('❌ [deactivateLeader] Error:', error.message);
      throw error;
    }
  }

  async reactivateLeader(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('▶️ [reactivateLeader] Reactivando líder inactivo ID:', leaderId);
      const response = await this.request(`/leaders/${leaderId}/reactivate`, { method: 'PUT' });
      log('✅ [reactivateLeader] Líder reactivado');
      return response;
    } catch (error) {
      logError('❌ [reactivateLeader] Error:', error.message);
      throw error;
    }
  }

  async updateLeader(leaderId, leaderType = null, cellGroupCode = null, notes = null) {
    try {
      validateId(leaderId, 'leaderId');
      const params = new URLSearchParams();
      if (leaderType) {
        validateString(leaderType, 'leaderType', 1, 50);
        params.append('leaderType', leaderType);
      }
      if (cellGroupCode) params.append('cellGroupCode', cellGroupCode.trim());
      if (notes) params.append('notes', notes.trim());
      log('📝 [updateLeader] Actualizando líder ID:', leaderId);
      const response = await this.request(`/leaders/${leaderId}?${params.toString()}`, { method: 'PUT' });
      log('✅ [updateLeader] Líder actualizado');
      return response;
    } catch (error) {
      logError('❌ [updateLeader] Error:', error.message);
      throw error;
    }
  }

  async getLeaderStatistics() {
    try {
      log('📊 [getLeaderStatistics] Obteniendo estadísticas');
      const response = await this.request('/leaders/statistics');
      log('✅ [getLeaderStatistics] Éxito -', { total: response?.totalLeaders, activos: response?.activeLeaders, suspendidos: response?.suspendedLeaders, inactivos: response?.inactiveLeaders });
      return response;
    } catch (error) {
      logError('❌ [getLeaderStatistics] Error:', error.message);
      throw error;
    }
  }

  async searchMembers(term) {
    try {
      if (!term || term.trim().length < 2) return [];
      validateString(term, 'searchTerm', 2, 100);
      log('🔍 [searchMembers] Buscando:', term);
      const [allMembers, leaders] = await Promise.all([
        this.getAllMembers(),
        this.getLeaders(),
      ]);
      const leaderMemberIds = new Set(leaders.map(l => l.memberId));
      const searchTerm = term.toLowerCase().trim();
      const results = allMembers
        .filter(member => {
          const matches =
            (member.name?.toLowerCase().includes(searchTerm)) ||
            (member.document?.toLowerCase().includes(searchTerm)) ||
            (member.email?.toLowerCase().includes(searchTerm)) ||
            (member.phone?.includes(searchTerm));
          return matches && !leaderMemberIds.has(member.id);
        })
        .map(member => ({ ...member, isLeader: false }))
        .slice(0, 20);
      log('✅ [searchMembers] Encontrados:', results.length);
      return results;
    } catch (error) {
      logError('❌ [searchMembers] Error:', error.message);
      throw error;
    }
  }

  async deleteLeader(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('🗑️ [deleteLeader] Eliminando líder ID:', leaderId);
      const response = await this.request(`/leaders/${leaderId}`, { method: 'DELETE' });
      log('✅ [deleteLeader] Líder eliminado permanentemente');
      return response;
    } catch (error) {
      logError('❌ [deleteLeader] Error:', error.message);
      throw error;
    }
  }

  // ========== 🏠 CÉLULAS ==========

  async createCell(cellData) {
    try {
      if (!cellData || typeof cellData !== 'object') throw new Error('Datos de célula inválidos');
      validateString(cellData.name, 'name', 3, 200);
      validateId(cellData.mainLeaderId, 'mainLeaderId');
      validateId(cellData.groupLeaderId, 'groupLeaderId');
      validateId(cellData.hostId, 'hostId');
      validateId(cellData.timoteoId, 'timoteoId');
      if (cellData.branchLeaderId !== null && cellData.branchLeaderId !== undefined && cellData.branchLeaderId !== '') {
        validateId(cellData.branchLeaderId, 'branchLeaderId');
      }
      if (cellData.maxCapacity !== null && cellData.maxCapacity !== undefined) {
        validateNumber(cellData.maxCapacity, 'maxCapacity', 1, 1000);
      }
      const payload = {
        name: cellData.name.trim(),
        mainLeaderId: Number(cellData.mainLeaderId),
        groupLeaderId: Number(cellData.groupLeaderId),
        hostId: Number(cellData.hostId),
        timoteoId: Number(cellData.timoteoId),
        meetingDay: cellData.meetingDay || null,
        meetingTime: cellData.meetingTime || null,
        meetingAddress: cellData.meetingAddress?.trim() || null,
        maxCapacity: cellData.maxCapacity ? Number(cellData.maxCapacity) : null,
        district: cellData.district?.trim() || null,
        notes: cellData.notes?.trim() || null
      };
      if (cellData.branchLeaderId && cellData.branchLeaderId !== '' && cellData.branchLeaderId !== 'null') {
        payload.branchLeaderId = Number(cellData.branchLeaderId);
      }
      log('🏠 [createCell] Creando célula:', { name: payload.name, mainLeaderId: payload.mainLeaderId });
      const response = await this.request('/cells', { method: 'POST', body: JSON.stringify(payload) });
      log('✅ [createCell] Éxito - ID:', response?.cellId || response?.id);
      return response;
    } catch (error) {
      logError('❌ [createCell] Error:', error.message);
      throw error;
    }
  }

  async getCells(ordered = true) {
    try {
      log('📋 [getCells] Obteniendo todas las células (ordered:', ordered, ')');
      const response = await this.request(`/cells?ordered=${ordered}`);
      log('✅ [getCells] Éxito -', response?.length || 0, 'células');
      return response;
    } catch (error) {
      logError('❌ [getCells] Error:', error.message);
      throw error;
    }
  }

  async getAccessibleCells() {
    try {
      log('📋 [getAccessibleCells] Obteniendo células accesibles para el usuario logueado');
      const response = await this.request('/attendance-cell-group/my-cells');
      log('✅ [getAccessibleCells] Éxito -', response?.length || 0, 'células');
      return response;
    } catch (error) {
      logError('❌ [getAccessibleCells] Error:', error.message);
      throw error;
    }
  }

  async getCellById(id) {
    try {
      validateId(id, 'cellId');
      log('🔍 [getCellById] Buscando célula ID:', id);
      return this.request(`/cells/${id}`);
    } catch (error) {
      logError('❌ [getCellById] Error:', error.message);
      throw error;
    }
  }

  async getCellMembers(cellId) {
    try {
      validateId(cellId, 'cellId');
      log('👥 [getCellMembers] Obteniendo miembros de célula ID:', cellId);
      const response = await this.request(`/cells/${cellId}/members`);
      const members = response?.members ?? [];
      log('✅ [getCellMembers] Miembros recibidos:', members.length);
      return members;
    } catch (error) {
      logError('❌ [getCellMembers] Error:', error.message);
      throw error;
    }
  }

  async getCellsByStatus(status, ordered = false) {
    try {
      validateString(status, 'status', 1, 50);
      log('🔍 [getCellsByStatus] Buscando células con estado:', status);
      return this.request(`/cells/status/${status}?ordered=${ordered}`);
    } catch (error) {
      logError('❌ [getCellsByStatus] Error:', error.message);
      throw error;
    }
  }

  async getCellsByLeader(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('🔍 [getCellsByLeader] Buscando células del líder ID:', leaderId);
      return this.request(`/cells/leader/${leaderId}`);
    } catch (error) {
      logError('❌ [getCellsByLeader] Error:', error.message);
      throw error;
    }
  }

  async getCellsByDistrict(district, status = null) {
    try {
      validateString(district, 'district', 1, 50);
      let url = `/cells/district/${district}`;
      if (status) url += `?status=${status}`;
      log('🔍 [getCellsByDistrict] Buscando células del distrito:', district);
      return this.request(url);
    } catch (error) {
      logError('❌ [getCellsByDistrict] Error:', error.message);
      throw error;
    }
  }

  async getAvailableCells(district = null) {
    try {
      let url = '/cells/available';
      if (district) url += `?district=${district}`;
      log('🔍 [getAvailableCells] Obteniendo células disponibles');
      return this.request(url);
    } catch (error) {
      logError('❌ [getAvailableCells] Error:', error.message);
      throw error;
    }
  }

  async getCellsWithIncompleteLeadership() {
    try {
      log('⚠️ [getCellsWithIncompleteLeadership] Obteniendo células con liderazgo incompleto');
      return this.request('/cells/incomplete-leadership');
    } catch (error) {
      logError('❌ [getCellsWithIncompleteLeadership] Error:', error.message);
      throw error;
    }
  }

  async getCellsWithCompleteLeadership() {
    try {
      log('✅ [getCellsWithCompleteLeadership] Obteniendo células con liderazgo completo');
      return this.request('/cells/complete-leadership');
    } catch (error) {
      logError('❌ [getCellsWithCompleteLeadership] Error:', error.message);
      throw error;
    }
  }

  async getActiveCellsWithAllLeaders() {
    try {
      log('🚀 [getActiveCellsWithAllLeaders] Obteniendo células activas con líderes');
      return this.request('/cells/active-with-leaders');
    } catch (error) {
      logError('❌ [getActiveCellsWithAllLeaders] Error:', error.message);
      throw error;
    }
  }

  async getFullCells() {
    try {
      log('🔍 [getFullCells] Obteniendo células llenas');
      return this.request('/cells/full');
    } catch (error) {
      logError('❌ [getFullCells] Error:', error.message);
      throw error;
    }
  }

  async searchCellsByName(name) {
    try {
      validateString(name, 'name', 2, 200);
      log('🔍 [searchCellsByName] Buscando células con nombre:', name);
      return this.request(`/cells/search?name=${encodeURIComponent(name)}`);
    } catch (error) {
      logError('❌ [searchCellsByName] Error:', error.message);
      throw error;
    }
  }

  async getCellsByMainLeader(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('🔍 [getCellsByMainLeader] Buscando células del líder de red ID:', leaderId);
      return this.request(`/cells/main-leader/${leaderId}`);
    } catch (error) {
      logError('❌ [getCellsByMainLeader] Error:', error.message);
      throw error;
    }
  }

  async getDirectCellsByMainLeader(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('🔍 [getDirectCellsByMainLeader] Buscando células directas del líder ID:', leaderId);
      return this.request(`/cells/main-leader/${leaderId}/direct`);
    } catch (error) {
      logError('❌ [getDirectCellsByMainLeader] Error:', error.message);
      throw error;
    }
  }

  async getHierarchicalCellsByMainLeader(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('🔍 [getHierarchicalCellsByMainLeader] Buscando células jerárquicas del líder ID:', leaderId);
      return this.request(`/cells/main-leader/${leaderId}/hierarchical`);
    } catch (error) {
      logError('❌ [getHierarchicalCellsByMainLeader] Error:', error.message);
      throw error;
    }
  }

  async getNetworkByMainLeader(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('🕸️ [getNetworkByMainLeader] Obteniendo red completa del líder:', leaderId);
      const response = await this.request(`/cells/main-leader/${leaderId}/network`);
      log('✅ [getNetworkByMainLeader] Red obtenida:', { totalCélulas: response?.cells?.length || 0 });
      return response;
    } catch (error) {
      logError('❌ [getNetworkByMainLeader] Error:', error.message);
      throw error;
    }
  }

  async getMainLeaderStatistics(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('📊 [getMainLeaderStatistics] Obteniendo estadísticas del líder ID:', leaderId);
      return this.request(`/cells/main-leader/${leaderId}/statistics`);
    } catch (error) {
      logError('❌ [getMainLeaderStatistics] Error:', error.message);
      throw error;
    }
  }

  async getCellsByBranchLeader(leaderId) {
    try {
      validateId(leaderId, 'leaderId');
      log('🔀 [getCellsByBranchLeader] Obteniendo células del líder de rama:', leaderId);
      const response = await this.request(`/cells/branch-leader/${leaderId}`);
      log('✅ [getCellsByBranchLeader] Éxito -', response?.cells?.length || 0, 'células');
      return response;
    } catch (error) {
      logError('❌ [getCellsByBranchLeader] Error:', error.message);
      throw error;
    }
  }

  async getCellsByMeetingDay(day) {
    try {
      validateString(day, 'day', 1, 20);
      log('🔍 [getCellsByMeetingDay] Buscando células que se reúnen el día:', day);
      return this.request(`/cells/meeting-day/${day}`);
    } catch (error) {
      logError('❌ [getCellsByMeetingDay] Error:', error.message);
      throw error;
    }
  }

  async getMultiplyingCells() {
    try {
      log('🌱 [getMultiplyingCells] Obteniendo células en multiplicación');
      return this.request('/cells/multiplying');
    } catch (error) {
      logError('❌ [getMultiplyingCells] Error:', error.message);
      throw error;
    }
  }

  async getTopMultiplyingCells(limit = 10) {
    try {
      validateNumber(limit, 'limit', 1, 100);
      log('🏆 [getTopMultiplyingCells] Obteniendo top', limit, 'células con más multiplicaciones');
      return this.request(`/cells/top-multiplying?limit=${limit}`);
    } catch (error) {
      logError('❌ [getTopMultiplyingCells] Error:', error.message);
      throw error;
    }
  }

  async getCellHierarchy(cellId) {
    try {
      validateId(cellId, 'cellId');
      log('🏛️ [getCellHierarchy] Obteniendo jerarquía de célula:', cellId);
      return this.request(`/cells/hierarchy/${cellId}`);
    } catch (error) {
      logError('❌ [getCellHierarchy] Error:', error.message);
      throw error;
    }
  }

  async updateCell(id, cellData) {
    try {
      validateId(id, 'cellId');
      if (!cellData || typeof cellData !== 'object') throw new Error('Datos de célula inválidos');
      if (cellData.name) validateString(cellData.name, 'name', 3, 200);
      if (cellData.mainLeaderId) validateId(cellData.mainLeaderId, 'mainLeaderId');
      if (cellData.branchLeaderId) validateId(cellData.branchLeaderId, 'branchLeaderId');
      if (cellData.groupLeaderId) validateId(cellData.groupLeaderId, 'groupLeaderId');
      if (cellData.hostId) validateId(cellData.hostId, 'hostId');
      if (cellData.timoteoId) validateId(cellData.timoteoId, 'timoteoId');
      if (cellData.maxCapacity) validateNumber(cellData.maxCapacity, 'maxCapacity', 1, 1000);
      const payload = {};
      const fields = ['name', 'mainLeaderId', 'branchLeaderId', 'groupLeaderId', 'hostId', 'timoteoId', 'meetingDay', 'meetingTime', 'meetingAddress', 'maxCapacity', 'district', 'notes'];
      fields.forEach(field => {
        if (cellData[field] !== undefined && cellData[field] !== null && cellData[field] !== '') {
          if (typeof cellData[field] === 'string') {
            payload[field] = cellData[field].trim();
          } else if (field.includes('Id') || field === 'maxCapacity') {
            payload[field] = Number(cellData[field]);
          } else {
            payload[field] = cellData[field];
          }
        }
      });
      if (payload.meetingTime && payload.meetingTime.length > 5) {
        payload.meetingTime = payload.meetingTime.substring(0, 5);
      }
      // Fix 2 — Asegurar que district sea el valor del enum, no la descripción
      const districtMap = {
        'Pastores': 'PASTORES',
        'Distrito 1': 'D1',
        'Distrito 2': 'D2',
        'Distrito 3': 'D3',
      };
      if (payload.district && districtMap[payload.district]) {
        payload.district = districtMap[payload.district];
      }
      log('📝 [updateCell] Actualizando célula ID:', id);
      const response = await this.request(`/cells/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
      log('✅ [updateCell] Célula actualizada');
      return response;
    } catch (error) {
      logError('❌ [updateCell] Error:', error.message);
      throw error;
    }
  }

  async changeCellStatus(id, newStatus) {
    try {
      validateId(id, 'cellId');
      validateString(newStatus, 'newStatus', 1, 50);
      log('🔄 [changeCellStatus] Cambiando estado de célula', { id, newStatus });
      const response = await this.request(`/cells/${id}/status`, { method: 'PUT', body: JSON.stringify({ newStatus }) });
      log('✅ [changeCellStatus] Estado cambiado');
      return response;
    } catch (error) {
      logError('❌ [changeCellStatus] Error:', error.message);
      throw error;
    }
  }

  async deleteCell(id) {
    try {
      validateId(id, 'cellId');
      log('🗑️ [deleteCell] Eliminando célula ID:', id);
      const response = await this.request(`/cells/${id}`, { method: 'DELETE' });
      log('✅ [deleteCell] Célula marcada como inactiva');
      return response;
    } catch (error) {
      logError('❌ [deleteCell] Error:', error.message);
      throw error;
    }
  }

  async addMemberToCell(cellId, memberId) {
    try {
      validateId(cellId, 'cellId');
      validateId(memberId, 'memberId');
      log('➕ [addMemberToCell] Agregando miembro', { cellId, memberId });
      const response = await this.request(`/cells/${cellId}/members`, { method: 'POST', body: JSON.stringify({ memberId }) });
      log('✅ [addMemberToCell] Miembro agregado');
      return response;
    } catch (error) {
      logError('❌ [addMemberToCell] Error:', error.message);
      throw error;
    }
  }

  async removeMemberFromCell(cellId, memberId) {
    try {
      validateId(cellId, 'cellId');
      validateId(memberId, 'memberId');
      log('➖ [removeMemberFromCell] Removiendo miembro', { cellId, memberId });
      const response = await this.request(`/cells/${cellId}/members/${memberId}`, { method: 'DELETE' });
      log('✅ [removeMemberFromCell] Miembro removido');
      return response;
    } catch (error) {
      logError('❌ [removeMemberFromCell] Error:', error.message);
      throw error;
    }
  }

  /**
   * Desvincular un líder de una célula manualmente.
   * DELETE /api/v1/cells/{cellId}/leaders/{leaderId}
   *
   * Funciona en cualquier CellStatus. El backend limpia el rol del líder,
   * actualiza su cellGroupCode a null y recalcula el estado de la célula.
   *
   * @param {number} cellId   - ID de la célula
   * @param {number} leaderId - ID del líder a desvincular
   * @returns {Object} Respuesta del backend con newCellStatus, newCellStatusDisplay
   *                   y missingOrInactiveLeaders (si aplica)
   */
  async unlinkLeaderFromCell(cellId, leaderId) {
    try {
      validateId(cellId, 'cellId');
      validateId(leaderId, 'leaderId');
      log('✂️ [unlinkLeaderFromCell] Desvinculando líder', { cellId, leaderId });
      const response = await this.request(`/cells/${cellId}/leaders/${leaderId}`, { method: 'DELETE' });
      log('✅ [unlinkLeaderFromCell] Éxito - Nuevo estado:', response?.newCellStatus);
      return response;
    } catch (error) {
      logError('❌ [unlinkLeaderFromCell] Error:', error.message);
      throw error;
    }
  }

  async startMultiplication(cellId) {
    try {
      validateId(cellId, 'cellId');
      log('🌱 [startMultiplication] Iniciando multiplicación célula ID:', cellId);
      const response = await this.request(`/cells/${cellId}/start-multiplication`, { method: 'POST' });
      log('✅ [startMultiplication] Multiplicación iniciada');
      return response;
    } catch (error) {
      logError('❌ [startMultiplication] Error:', error.message);
      throw error;
    }
  }

  async completeMultiplication(cellId) {
    try {
      validateId(cellId, 'cellId');
      log('✅ [completeMultiplication] Completando multiplicación célula ID:', cellId);
      const response = await this.request(`/cells/${cellId}/complete-multiplication`, { method: 'POST' });
      log('✅ [completeMultiplication] Multiplicación completada');
      return response;
    } catch (error) {
      logError('❌ [completeMultiplication] Error:', error.message);
      throw error;
    }
  }

  async verifyCell(cellId) {
    try {
      validateId(cellId, 'cellId');
      log('🔍 [verifyCell] Verificando célula ID:', cellId);
      const response = await this.request(`/cells/${cellId}/verify`, { method: 'POST' });
      log('✅ [verifyCell] Célula verificada');
      return response;
    } catch (error) {
      logError('❌ [verifyCell] Error:', error.message);
      throw error;
    }
  }

  async verifyAllCells() {
    try {
      log('🔄 [verifyAllCells] Verificando todas las células');
      const response = await this.request('/cells/verify-all', { method: 'POST' });
      log('✅ [verifyAllCells] Éxito -', { total: response?.totalVerified, cambiaron: response?.statusChanged });
      return response;
    } catch (error) {
      logError('❌ [verifyAllCells] Error:', error.message);
      throw error;
    }
  }

  async getCellStatistics() {
    try {
      log('📊 [getCellStatistics] Obteniendo estadísticas de células');
      const response = await this.request('/cells/statistics');
      log('✅ [getCellStatistics] Éxito -', { total: response?.totalCells, activas: response?.activeCells, incompletas: response?.incompleteCells });
      return response;
    } catch (error) {
      logError('❌ [getCellStatistics] Error:', error.message);
      throw error;
    }
  }

  // ========== 📊 ESTADÍSTICAS ==========

  async getStatisticsByLevelAndYear() {
    try {
      log('📊 [getStatisticsByLevelAndYear] Iniciando');
      const enrollments = await this.getEnrollments();
      log(`📋 Cohortes obtenidas: ${enrollments.length}`);

      if (!enrollments.length) return {};

      // ✅ TODAS las cohortes en paralelo en lugar de secuencial
      const results = await Promise.allSettled(
        enrollments.map(enrollment =>
          this.getStudentEnrollmentsByEnrollment(enrollment.id)
            .then(students => ({ enrollment, students: students || [] }))
        )
      );

      const levelYearData = {};

      results.forEach(result => {
        if (result.status === 'rejected') {
          console.warn('⚠️ Cohorte falló:', result.reason?.message);
          return;
        }

        const { enrollment, students } = result.value;
        const cohortName = enrollment.cohortName || enrollment.name;

        if (!students.length) {
          log(`⚠️ ${cohortName} - Sin estudiantes`);
          return;
        }

        log(`✅ ${cohortName} - ${students.length} estudiantes`);

        students.forEach(student => {
          // Extraer año
          let year = 'SIN_AÑO';
          const rawDate = student.enrollmentDate || student.enrollment_date;
          if (rawDate) {
            const extractedYear = new Date(rawDate).getFullYear();
            if (!isNaN(extractedYear) && extractedYear > 1900) {
              year = extractedYear.toString();
            }
          }

          const level = student.levelEnrollment || student.level || 'SIN_NIVEL';

          if (!levelYearData[year]) levelYearData[year] = {};
          if (!levelYearData[year][level]) {
            levelYearData[year][level] = {
              label: this.getLevelLabel(level),
              levelEnrollment: level,
              total: 0, passed: 0, failed: 0, pending: 0,
            };
          }

          const entry = levelYearData[year][level];
          entry.total += 1;
          if (student.passed === true) entry.passed += 1;
          else if (student.passed === false) entry.failed += 1;
          else entry.pending += 1;
        });
      });

      // Construir resultado ordenado
      const LEVEL_ORDER = [
        'PREENCUENTRO', 'ENCUENTRO', 'POST_ENCUENTRO', 'BAUTIZOS',
        'ESENCIA_1', 'ESENCIA_2', 'ESENCIA_3', 'SANIDAD_INTEGRAL_RAICES',
        'ESENCIA_4', 'ADIESTRAMIENTO', 'GRADUACION',
      ];

      const result = {};
      Object.keys(levelYearData)
        .sort((a, b) => {
          if (a === 'SIN_AÑO') return 1;
          if (b === 'SIN_AÑO') return -1;
          return b - a; // años descendente
        })
        .forEach(year => {
          result[year] = {};
          LEVEL_ORDER.forEach(levelKey => {
            const levelData = levelYearData[year][levelKey];
            if (!levelData) return;
            result[year][levelKey] = {
              label: levelData.label,
              total: levelData.total,
              passed: levelData.passed,
              failed: levelData.failed,
              pending: levelData.pending,
              passPercentage: levelData.total > 0
                ? parseFloat(((levelData.passed / levelData.total) * 100).toFixed(1))
                : 0,
            };
          });
        });

      log('✅ [getStatisticsByLevelAndYear] Completado');
      return result;

    } catch (error) {
      logError('❌ [getStatisticsByLevelAndYear] Error:', error);
      throw error;
    }
  }

  getLevelLabel(levelEnrollment) {
    const levelMap = { 'PREENCUENTRO': 'Pre-encuentro', 'ENCUENTRO': 'Encuentro', 'POST_ENCUENTRO': 'Post-encuentro', 'BAUTIZOS': 'Bautizos', 'ESENCIA_1': 'ESENCIA 1', 'ESENCIA_2': 'ESENCIA 2', 'ESENCIA_3': 'ESENCIA 3', 'SANIDAD_INTEGRAL_RAICES': 'Sanidad Integral Raíces', 'ESENCIA_4': 'ESENCIA 4', 'ADIESTRAMIENTO': 'Adiestramiento', 'GRADUACION': 'Graduación' };
    return levelMap[levelEnrollment] || levelEnrollment;
  }

  // ========== 📋 ASISTENCIAS DE CÉLULAS ==========

  async getAttendanceConfig() {
    try {
      log('📋 [getAttendanceConfig] Obteniendo configuración');
      return this.request('/attendance-cell-group/config');
    } catch (error) {
      logError('❌ [getAttendanceConfig] Error:', error.message);
      throw error;
    }
  }

  async generateCellAttendances(cellId, date) {
    try {
      validateId(cellId, 'cellId');
      validateString(date, 'date', 10, 10);
      log('📋 [generateCellAttendances] Generando para célula:', cellId, 'fecha:', date);
      const response = await this.request(`/attendance-cell-group/generate/cell/${cellId}?date=${date}`, { method: 'POST' });
      log('✅ [generateCellAttendances] Éxito -', response?.totalCount || 0, 'registros');
      return response;
    } catch (error) {
      logError('❌ [generateCellAttendances] Error:', error.message);
      throw error;
    }
  }

  async generateMyCellsAttendances(date) {
    try {
      validateString(date, 'date', 10, 10);
      log('📋 [generateMyCellsAttendances] Generando para mis células, fecha:', date);
      const response = await this.request(`/attendance-cell-group/generate/my-cells?date=${date}`, { method: 'POST' });
      log('✅ [generateMyCellsAttendances] Éxito -', response?.totalCells || 0, 'células');
      return response;
    } catch (error) {
      logError('❌ [generateMyCellsAttendances] Error:', error.message);
      throw error;
    }
  }

  async generateCurrentMonthAttendances() {
    try {
      log('🔄 [generateCurrentMonthAttendances] Ejecutando generación manual');
      const response = await this.request('/attendance-cell-group/generate/current-month', { method: 'POST' });
      log('✅ [generateCurrentMonthAttendances] Éxito');
      return response;
    } catch (error) {
      logError('❌ [generateCurrentMonthAttendances] Error:', error.message);
      throw error;
    }
  }

  async getCellAttendancesCurrentMonth() {
    try {
      log('📅 [getCellAttendancesCurrentMonth] Consultando mes actual');
      const response = await this.request('/attendance-cell-group/current-month');
      log('✅ [getCellAttendancesCurrentMonth] Éxito -', response?.totalCells || 0, 'células');
      return response;
    } catch (error) {
      logError('❌ [getCellAttendancesCurrentMonth] Error:', error.message);
      throw error;
    }
  }

  async getCellAttendancesByMonth(year, month) {
    try {
      validateNumber(year, 'year', 2020);
      validateNumber(month, 'month', 1, 12);
      log('📅 [getCellAttendancesByMonth] Consultando:', year, '/', month);
      return this.request(`/attendance-cell-group/month/${year}/${month}`);
    } catch (error) {
      logError('❌ [getCellAttendancesByMonth] Error:', error.message);
      throw error;
    }
  }

  async getCellAttendancesByDate(cellId, date) {
    try {
      validateId(cellId, 'cellId');
      validateString(date, 'date', 10, 10);
      log('🔍 [getCellAttendancesByDate] Célula:', cellId, 'Fecha:', date);
      const response = await this.request(`/attendance-cell-group/cell/${cellId}/date/${date}`);
      log('✅ [getCellAttendancesByDate] Éxito -', response?.totalCount || 0, 'registros');
      return response;
    } catch (error) {
      logError('❌ [getCellAttendancesByDate] Error:', error.message);
      throw error;
    }
  }

  async recordCellAttendance(cellId, date, attendanceData) {
    try {
      validateId(cellId, 'cellId');
      validateString(date, 'date', 10, 10);
      if (!attendanceData || typeof attendanceData !== 'object') throw new Error('Datos de asistencia inválidos');
      log('📝 [recordCellAttendance] Registrando:', { cellId, date, memberId: attendanceData.memberId });
      const response = await this.request(`/attendance-cell-group/cell/${cellId}/date/${date}`, { method: 'POST', body: JSON.stringify(attendanceData) });
      log('✅ [recordCellAttendance] Éxito');
      return response;
    } catch (error) {
      logError('❌ [recordCellAttendance] Error:', error.message);
      throw error;
    }
  }

  async recordBulkCellAttendances(cellId, bulkData) {
    try {
      validateId(cellId, 'cellId');
      if (!bulkData || typeof bulkData !== 'object') throw new Error('Datos de asistencias inválidos');
      log('📦 [recordBulkCellAttendances] Registrando masivo:', { cellId, date: bulkData.attendanceDate, count: bulkData.attendances?.length || 0 });
      const response = await this.request(`/attendance-cell-group/cell/${cellId}/bulk`, { method: 'POST', body: JSON.stringify(bulkData) });
      log('✅ [recordBulkCellAttendances] Éxito -', response?.totalCount || 0, 'registros');
      return response;
    } catch (error) {
      logError('❌ [recordBulkCellAttendances] Error:', error.message);
      throw error;
    }
  }

  async updateBulkCellAttendances(cellId, date, attendances) {
    try {
      validateId(cellId, 'cellId');
      validateString(date, 'date', 10, 10);
      if (!Array.isArray(attendances)) throw new Error('Datos de asistencias inválidos');
      log('📦 [updateBulkCellAttendances] Actualizando:', { cellId, date, count: attendances.length });
      const response = await this.request(`/attendance-cell-group/cell/${cellId}/date/${date}/bulk`, { method: 'PUT', body: JSON.stringify(attendances) });
      log('✅ [updateBulkCellAttendances] Éxito -', response?.totalCount || 0, 'registros');
      return response;
    } catch (error) {
      logError('❌ [updateBulkCellAttendances] Error:', error.message);
      throw error;
    }
  }

  async getCellAttendanceSummary(cellId, date) {
    try {
      validateId(cellId, 'cellId');
      validateString(date, 'date', 10, 10);
      log('📊 [getCellAttendanceSummary] Resumen:', { cellId, date });
      return this.request(`/attendance-cell-group/summary/cell/${cellId}/date/${date}`);
    } catch (error) {
      logError('❌ [getCellAttendanceSummary] Error:', error.message);
      throw error;
    }
  }

  async getCellAttendanceMonthlyStats(cellId, year, month) {
    try {
      validateId(cellId, 'cellId');
      validateNumber(year, 'year', 2020);
      validateNumber(month, 'month', 1, 12);
      log('📊 [getCellAttendanceMonthlyStats] Stats:', { cellId, year, month });
      return this.request(`/attendance-cell-group/statistics/cell/${cellId}/month/${year}/${month}`);
    } catch (error) {
      logError('❌ [getCellAttendanceMonthlyStats] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener estadísticas globales de asistencia (consolidadas)
   * GET /api/v1/attendance-cell-group/statistics/global
   */
  async getCellAttendanceGlobalStats() {
    try {
      log('📊 [getCellAttendanceGlobalStats] Obteniendo resumen consolidado global');
      return await this.request('/attendance-cell-group/statistics/global');
    } catch (error) {
      logError('❌ [getCellAttendanceGlobalStats] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener todas las asistencias de un mes para todas las células
   * Usado para la agregación local en las estadísticas globales
   * GET /api/v1/attendance-cell-group/month/{year}/{month}
   */
  async getMonthAttendances(year, month) {
    try {
      validateNumber(year, 'year', 2020);
      validateNumber(month, 'month', 1, 12);
      log('📅 [getMonthAttendances] Consultando detalle de red:', year, '/', month);
      return await this.request(`/attendance-cell-group/month/${year}/${month}`);
    } catch (error) {
      logError('❌ [getMonthAttendances] Error:', error.message);
      throw error;
    }
  }

  // ========== 📅 EVENTOS DE ASISTENCIA ==========

  /**
   * Obtener todos los eventos de asistencia activos
   * GET /api/v1/attendance-cell-group/events
   */
  async getActiveAttendanceEvents() {
    try {
      log('📅 [getActiveAttendanceEvents] Obteniendo eventos activos');
      const response = await this.request('/attendance-cell-group/events');
      log('✅ [getActiveAttendanceEvents] Éxito -', response?.events?.length || 0, 'eventos');
      return response;
    } catch (error) {
      logError('❌ [getActiveAttendanceEvents] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener todos los eventos de asistencia (activos e inactivos)
   * GET /api/v1/attendance-cell-group/events/all
   * Solo para PASTORES y CONEXION
   */
  async getAllAttendanceEvents() {
    try {
      log('📅 [getAllAttendanceEvents] Obteniendo todos los eventos');
      const response = await this.request('/attendance-cell-group/events/all');
      log('✅ [getAllAttendanceEvents] Éxito -', response?.events?.length || 0, 'eventos');
      return response;
    } catch (error) {
      logError('❌ [getAllAttendanceEvents] Error:', error.message);
      throw error;
    }
  }

  /**
   * Crear un nuevo evento de asistencia especial
   * POST /api/v1/attendance-cell-group/events
   * Solo para PASTORES y CONEXION
   * 
   * @param {Object} eventData - Datos del evento
   * @param {string} eventData.name - Nombre del evento (obligatorio)
   * @param {string} eventData.description - Descripción (opcional)
   * @param {Array<string>} eventData.eventDates - Lista de fechas en formato YYYY-MM-DD (obligatorio)
   */
  async createAttendanceEvent(eventData) {
    try {
      if (!eventData || typeof eventData !== 'object') {
        throw new Error('Datos del evento inválidos');
      }

      validateString(eventData.name, 'name', 1, 200);

      if (!eventData.eventDates || !Array.isArray(eventData.eventDates) || eventData.eventDates.length === 0) {
        throw new Error('Debe especificar al menos una fecha para el evento');
      }

      // Validar que todas las fechas tengan formato correcto
      eventData.eventDates.forEach(date => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          throw new Error(`Formato de fecha inválido: ${date}. Use YYYY-MM-DD`);
        }
      });

      log('📅 [createAttendanceEvent] Creando evento:', { name: eventData.name, fechas: eventData.eventDates.length });

      const payload = {
        name: eventData.name.trim(),
        description: eventData.description?.trim() || null,
        eventDates: eventData.eventDates
      };

      const response = await this.request('/attendance-cell-group/events', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      log('✅ [createAttendanceEvent] Éxito - ID:', response?.event?.id);
      return response;
    } catch (error) {
      logError('❌ [createAttendanceEvent] Error:', error.message);
      throw error;
    }
  }

  /**
   * Desactivar un evento de asistencia
   * DELETE /api/v1/attendance-cell-group/events/{eventId}
   * Solo para PASTORES y CONEXION
   */
  async deactivateAttendanceEvent(eventId) {
    try {
      validateId(eventId, 'eventId');
      log('🚫 [deactivateAttendanceEvent] Desactivando evento ID:', eventId);
      const response = await this.request(`/attendance-cell-group/events/${eventId}`, {
        method: 'DELETE'
      });
      log('✅ [deactivateAttendanceEvent] Evento desactivado');
      return response;
    } catch (error) {
      logError('❌ [deactivateAttendanceEvent] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener datos de sesión de una célula en una fecha específica
   * GET /api/v1/attendance-cell-group/cell/{cellId}/date/{date}/session-data
   */
  async getSessionData(cellId, date) {
    try {
      validateId(cellId, 'cellId');
      validateString(date, 'date', 10, 10);
      log('📊 [getSessionData] Obteniendo datos de sesión:', { cellId, date });
      const response = await this.request(`/attendance-cell-group/cell/${cellId}/date/${date}/session-data`);
      log('✅ [getSessionData] Éxito -', response?.hasData ? 'tiene datos' : 'sin datos');
      return response;
    } catch (error) {
      logError('❌ [getSessionData] Error:', error.message);
      throw error;
    }
  }

  /**
   * Guardar o actualizar datos de sesión de una reunión
   * PUT /api/v1/attendance-cell-group/cell/{cellId}/date/{date}/session-data
   * 
   * @param {number} cellId - ID de la célula
   * @param {string} date - Fecha en formato YYYY-MM-DD
   * @param {Object} payload - Datos de sesión
   * @param {number} payload.newParticipants - Nuevas visitas (opcional, default 0)
   * @param {number} payload.totalAttendees - Total asistentes (opcional, se calcula si se omite)
   * @param {string} payload.notes - Notas (opcional, max 500)
   */
  async saveSessionData(cellId, date, payload) {
    try {
      validateId(cellId, 'cellId');
      validateString(date, 'date', 10, 10);

      if (!payload || typeof payload !== 'object') {
        throw new Error('Datos de sesión inválidos');
      }

      // Validar newParticipants
      if (payload.newParticipants !== undefined && payload.newParticipants !== null && payload.newParticipants !== '') {
        const newParts = parseInt(payload.newParticipants, 10);
        if (isNaN(newParts) || newParts < 0) {
          throw new Error('newParticipants debe ser un número no negativo');
        }
      }

      // Validar totalAttendees
      if (payload.totalAttendees !== undefined && payload.totalAttendees !== null && payload.totalAttendees !== '') {
        const total = parseInt(payload.totalAttendees, 10);
        if (isNaN(total) || total < 0) {
          throw new Error('totalAttendees debe ser un número no negativo');
        }
      }

      // Validar notes
      if (payload.notes && payload.notes.length > 500) {
        throw new Error('Las notas no pueden superar 500 caracteres');
      }

      log('📝 [saveSessionData] Guardando datos de sesión:', {
        cellId,
        date,
        newParticipants: payload.newParticipants,
        totalAttendees: payload.totalAttendees
      });

      const response = await this.request(`/attendance-cell-group/cell/${cellId}/date/${date}/session-data`, {
        method: 'PUT',
        body: JSON.stringify({
          newParticipants: payload.newParticipants !== '' ? parseInt(payload.newParticipants, 10) : null,
          totalAttendees: payload.totalAttendees !== '' ? parseInt(payload.totalAttendees, 10) : null,
          notes: payload.notes || null
        })
      });

      log('✅ [saveSessionData] Datos guardados');
      return response;
    } catch (error) {
      logError('❌ [saveSessionData] Error:', error.message);
      throw error;
    }
  }

  // ========== 🔍 HEALTH CHECK ==========

  async healthCheck() {
    try {
      log('🏥 [healthCheck] Verificando estado del servicio');
      return this.request('/attendance-cell-group/health');
    } catch (error) {
      logError('❌ [healthCheck] Error:', error.message);
      throw error;
    }
  }

  // ========== 🔔 NOTIFICACIONES ==========

  /**
   * Conteo de notificaciones sin leer de un usuario.
   * GET /notifications/user/{userId}/unread-count
   * Requiere rol: PASTORES, ESENCIA, CONEXION, DESPLIEGUE, LIDER  (según NotificationController)
   * Retorna: { unreadCount: number }
   */
  async getUnreadNotificationCount(userId) {
    try {
      validateId(userId, 'userId');
      return this.request(`/notifications/user/${userId}/unread-count`);
    } catch (error) {
      logError('❌ [getUnreadNotificationCount] Error:', error.message);
      throw error;
    }
  }

  /**
   * Notificaciones activas (no archivadas) de un usuario.
   * GET /notifications/user/{userId}/active
   * Retorna: NotificationResponse[]
   */
  async getActiveNotifications(userId) {
    try {
      validateId(userId, 'userId');
      return this.request(`/notifications/user/${userId}/active`);
    } catch (error) {
      logError('❌ [getActiveNotifications] Error:', error.message);
      throw error;
    }
  }

  /**
   * Todas las notificaciones de un usuario.
   * GET /notifications/user/{userId}
   * Retorna: NotificationResponse[]
   */
  async getAllNotifications(userId) {
    try {
      validateId(userId, 'userId');
      return this.request(`/notifications/user/${userId}`);
    } catch (error) {
      logError('❌ [getAllNotifications] Error:', error.message);
      throw error;
    }
  }

  /**
   * Notificaciones sin leer de un usuario.
   * GET /notifications/user/{userId}/unread
   * Retorna: NotificationResponse[]
   */
  async getUnreadNotifications(userId) {
    try {
      validateId(userId, 'userId');
      return this.request(`/notifications/user/${userId}/unread`);
    } catch (error) {
      logError('❌ [getUnreadNotifications] Error:', error.message);
      throw error;
    }
  }

  /**
   * Marca una notificación como leída.
   * PUT /notifications/{id}/mark-read
   */
  async markNotificationAsRead(notificationId) {
    try {
      validateId(notificationId, 'notificationId');
      return this.request(`/notifications/${notificationId}/mark-read`, {
        method: 'PUT',
      });
    } catch (error) {
      logError('❌ [markNotificationAsRead] Error:', error.message);
      throw error;
    }
  }

  /**
   * Marca TODAS las notificaciones de un usuario como leídas.
   * PUT /notifications/user/{userId}/mark-all-read
   */
  async markAllNotificationsAsRead(userId) {
    try {
      validateId(userId, 'userId');
      return this.request(`/notifications/user/${userId}/mark-all-read`, {
        method: 'PUT',
      });
    } catch (error) {
      logError('❌ [markAllNotificationsAsRead] Error:', error.message);
      throw error;
    }
  }

  /**
   * Archiva una notificación.
   * PUT /notifications/{id}/archive
   * Solo PASTORES.
   */
  async archiveNotification(notificationId) {
    try {
      validateId(notificationId, 'notificationId');
      return this.request(`/notifications/${notificationId}/archive`, {
        method: 'PUT',
      });
    } catch (error) {
      logError('❌ [archiveNotification] Error:', error.message);
      throw error;
    }
  }

  /**
   * Elimina una notificación permanentemente.
   * DELETE /notifications/{id}
   * Solo PASTORES.
   */
  async deleteNotification(notificationId) {
    try {
      validateId(notificationId, 'notificationId');
      return this.request(`/notifications/${notificationId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      logError('❌ [deleteNotification] Error:', error.message);
      throw error;
    }
  }

  /**
   * Estadísticas de notificaciones de un usuario.
   * GET /notifications/user/{userId}/statistics
   * Solo PASTORES.
   */
  async getNotificationStatistics(userId) {
    try {
      validateId(userId, 'userId');
      return this.request(`/notifications/user/${userId}/statistics`);
    } catch (error) {
      logError('❌ [getNotificationStatistics] Error:', error.message);
      throw error;
    }
  }

  // ============================================================
  // ⛪ MÓDULO FINANCIERO IGLESIA
  // Base URL del módulo: /api/v1/finance
  // ============================================================

  // ========== 📌 GASTOS FIJOS ==========

  /**
   * Crear plantilla de gasto fijo mensual.
   * POST /finance/fixed-expenses
   * @param {Object} dto - FixedExpenseDTO { name, description, defaultAmount, category, isActive, createdBy }
   */
  async createFixedExpense(dto) {
    try {
      if (!dto || typeof dto !== 'object') throw new Error('Datos de gasto fijo inválidos');
      validateString(dto.name, 'name', 3, 150);
      validateNumber(dto.defaultAmount, 'defaultAmount', 0.01);
      validateString(dto.category, 'category', 1, 60);
      validateString(dto.createdBy, 'createdBy', 1, 100);

      log('📌 [createFixedExpense] Creando:', dto.name);
      const response = await this.request('/finance/fixed-expenses', {
        method: 'POST',
        body: JSON.stringify(dto),
      });
      log('✅ [createFixedExpense] Éxito - ID:', response?.id);
      return response;
    } catch (error) {
      logError('❌ [createFixedExpense] Error:', error.message);
      throw error;
    }
  }

  /**
   * Actualizar plantilla de gasto fijo.
   * PUT /finance/fixed-expenses/{id}
   * @param {number} id
   * @param {Object} dto - FixedExpenseDTO
   */
  async updateFixedExpense(id, dto) {
    try {
      validateId(id, 'fixedExpenseId');
      if (!dto || typeof dto !== 'object') throw new Error('Datos de gasto fijo inválidos');

      log('📝 [updateFixedExpense] Actualizando ID:', id);
      const response = await this.request(`/finance/fixed-expenses/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dto),
      });
      log('✅ [updateFixedExpense] Éxito');
      return response;
    } catch (error) {
      logError('❌ [updateFixedExpense] Error:', error.message);
      throw error;
    }
  }

  /**
   * Desactivar gasto fijo (borrado lógico).
   * El gasto deja de generar registros futuros; historial conservado.
   * PATCH /finance/fixed-expenses/{id}/deactivate
   * @param {number} id
   */
  async deactivateFixedExpense(id) {
    try {
      validateId(id, 'fixedExpenseId');
      log('⏹️ [deactivateFixedExpense] Desactivando ID:', id);
      const response = await this.request(`/finance/fixed-expenses/${id}/deactivate`, {
        method: 'PATCH',
      });
      log('✅ [deactivateFixedExpense] Éxito');
      return response;
    } catch (error) {
      logError('❌ [deactivateFixedExpense] Error:', error.message);
      throw error;
    }
  }

  /**
   * Eliminar gasto fijo permanentemente.
   * Solo si NO tiene registros mensuales históricos; preferir deactivateFixedExpense.
   * DELETE /finance/fixed-expenses/{id}
   * @param {number} id
   */
  async deleteFixedExpense(id) {
    try {
      validateId(id, 'fixedExpenseId');
      log('🗑️ [deleteFixedExpense] Eliminando ID:', id);
      const response = await this.request(`/finance/fixed-expenses/${id}`, {
        method: 'DELETE',
      });
      log('✅ [deleteFixedExpense] Éxito');
      return response;
    } catch (error) {
      logError('❌ [deleteFixedExpense] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener gasto fijo por ID.
   * GET /finance/fixed-expenses/{id}
   * @param {number} id
   */
  async getFixedExpenseById(id) {
    try {
      validateId(id, 'fixedExpenseId');
      log('🔍 [getFixedExpenseById] Buscando ID:', id);
      return this.request(`/finance/fixed-expenses/${id}`);
    } catch (error) {
      logError('❌ [getFixedExpenseById] Error:', error.message);
      throw error;
    }
  }

  /**
   * Listar todos los gastos fijos.
   * GET /finance/fixed-expenses?activeOnly=false|true
   * @param {boolean} activeOnly - true para solo los activos (default: false)
   */
  async getAllFixedExpenses(activeOnly = false) {
    try {
      log('📋 [getAllFixedExpenses] activeOnly:', activeOnly);
      const response = await this.request(`/finance/fixed-expenses?activeOnly=${activeOnly}`);
      log('✅ [getAllFixedExpenses] Éxito -', response?.length || 0, 'registros');
      return response;
    } catch (error) {
      logError('❌ [getAllFixedExpenses] Error:', error.message);
      throw error;
    }
  }

  /**
   * Listar solo los gastos fijos activos (alias conveniente).
   * GET /finance/fixed-expenses?activeOnly=true
   */
  async getActiveFixedExpenses() {
    return this.getAllFixedExpenses(true);
  }

  // ========== 📋 REGISTROS MENSUALES DE GASTOS FIJOS ==========

  /**
   * Obtener (y auto-generar si es la primera vez) los registros
   * de gastos fijos para un mes/año dado.
   * GET /finance/monthly-records?month={month}&year={year}
   * @param {number} month - 1-12
   * @param {number} year  - ≥ 2000
   */
  async getOrCreateMonthlyRecords(month, year) {
    try {
      validateNumber(month, 'month', 1, 12);
      validateNumber(year, 'year', 2000);
      log('📋 [getOrCreateMonthlyRecords]', { month, year });
      const response = await this.request(`/finance/monthly-records?month=${month}&year=${year}`);
      log('✅ [getOrCreateMonthlyRecords] Éxito -', response?.length || 0, 'registros');
      return response;
    } catch (error) {
      logError('❌ [getOrCreateMonthlyRecords] Error:', error.message);
      throw error;
    }
  }

  /**
   * Actualizar monto, estado de pago y/o notas de un registro mensual.
   * Permite ajustar el valor de un mes sin alterar la plantilla base.
   * PATCH /finance/monthly-records/{id}
   * @param {number}  id
   * @param {number|null}  newAmount - nuevo monto (null = no cambiar)
   * @param {boolean|null} isPaid    - estado de pago (null = no cambiar)
   * @param {string|null}  notes     - notas (null = no cambiar)
   */
  async updateMonthlyRecord(id, newAmount = null, isPaid = null, notes = null) {
    try {
      validateId(id, 'monthlyRecordId');

      const body = {};
      if (newAmount !== null) {
        validateNumber(newAmount, 'amount', 0.01);
        body.amount = newAmount;
      }
      if (isPaid !== null) body.isPaid = isPaid;
      if (notes !== null) body.notes = notes;

      log('📝 [updateMonthlyRecord] Actualizando ID:', id, body);
      const response = await this.request(`/finance/monthly-records/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      log('✅ [updateMonthlyRecord] Éxito');
      return response;
    } catch (error) {
      logError('❌ [updateMonthlyRecord] Error:', error.message);
      throw error;
    }
  }

  // ========== ⚡ GASTOS OCASIONALES ==========

  /**
 * Registrar un gasto no recurrente.
 * POST /finance/occasional-expenses
 */
  async createOccasionalExpense(dto) {
    try {
      if (!dto || typeof dto !== 'object') throw new Error('Datos de gasto ocasional inválidos');
      validateString(dto.name, 'name', 3, 150);

      // ✅ CORREGIDO: Validar y procesar el monto
      const numAmount = Number(dto.amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        throw new Error('El monto debe ser un número válido mayor a 0');
      }
      const validatedAmount = Math.round(numAmount * 100) / 100;

      validateString(dto.category, 'category', 1, 60);
      validateString(dto.expenseDate, 'expenseDate', 10, 10);
      validateString(dto.recordedBy, 'recordedBy', 1, 100);

      const processedDto = {
        ...dto,
        amount: validatedAmount
      };

      log('⚡ [createOccasionalExpense] Creando:', dto.name, 'monto:', validatedAmount);
      const response = await this.request('/finance/occasional-expenses', {
        method: 'POST',
        body: JSON.stringify(processedDto),
      });
      log('✅ [createOccasionalExpense] Éxito - ID:', response?.id);
      return response;
    } catch (error) {
      logError('❌ [createOccasionalExpense] Error:', error.message);
      throw error;
    }
  }

  /**
 * Actualizar gasto ocasional.
 * PUT /finance/occasional-expenses/{id}
 * @param {number} id
 * @param {Object} dto - OccasionalExpenseDTO
 */
  async updateOccasionalExpense(id, dto) {
    try {
      validateId(id, 'occasionalExpenseId');
      if (!dto || typeof dto !== 'object') throw new Error('Datos de gasto ocasional inválidos');

      // ✅ CORREGIDO: Procesar el monto correctamente
      const processedDto = { ...dto };

      // Asegurar que el monto sea un número con 2 decimales
      if (processedDto.amount !== undefined && processedDto.amount !== null) {
        const numAmount = Number(processedDto.amount);
        if (isNaN(numAmount)) {
          throw new Error('El monto debe ser un número válido');
        }
        // Redondear a 2 decimales para evitar problemas de precisión
        processedDto.amount = Math.round(numAmount * 100) / 100;
      }

      log('📝 [updateOccasionalExpense] Actualizando ID:', id, 'monto:', processedDto.amount);
      const response = await this.request(`/finance/occasional-expenses/${id}`, {
        method: 'PUT',
        body: JSON.stringify(processedDto),
      });
      log('✅ [updateOccasionalExpense] Éxito');
      return response;
    } catch (error) {
      logError('❌ [updateOccasionalExpense] Error:', error.message);
      throw error;
    }
  }

  /**
   * Eliminar gasto ocasional.
   * DELETE /finance/occasional-expenses/{id}
   * @param {number} id
   */
  async deleteOccasionalExpense(id) {
    try {
      validateId(id, 'occasionalExpenseId');
      log('🗑️ [deleteOccasionalExpense] Eliminando ID:', id);
      const response = await this.request(`/finance/occasional-expenses/${id}`, {
        method: 'DELETE',
      });
      log('✅ [deleteOccasionalExpense] Éxito');
      return response;
    } catch (error) {
      logError('❌ [deleteOccasionalExpense] Error:', error.message);
      throw error;
    }
  }

  /**
   * Listar gastos ocasionales de un mes/año.
   * GET /finance/occasional-expenses?month={month}&year={year}
   * @param {number} month - 1-12
   * @param {number} year  - ≥ 2000
   */
  async getOccasionalExpensesByMonth(month, year) {
    try {
      validateNumber(month, 'month', 1, 12);
      validateNumber(year, 'year', 2000);
      log('⚡ [getOccasionalExpensesByMonth]', { month, year });
      const response = await this.request(`/finance/occasional-expenses?month=${month}&year=${year}`);
      log('✅ [getOccasionalExpensesByMonth] Éxito -', response?.length || 0, 'registros');
      return response;
    } catch (error) {
      logError('❌ [getOccasionalExpensesByMonth] Error:', error.message);
      throw error;
    }
  }

  // ========== 📊 BALANCE MENSUAL ==========

  /**
   * Obtener el balance mensual completo.
   * Incluye ingresos, egresos, saldo anterior, balance final e indicadores.
   * GET /finance/balance?month={month}&year={year}
   * @param {number} month - 1-12
   * @param {number} year  - ≥ 2000
   * @returns {MonthlyBalanceDTO}
   */
  async getMonthlyBalance(month, year) {
    try {
      validateNumber(month, 'month', 1, 12);
      validateNumber(year, 'year', 2000);
      log('📊 [getMonthlyBalance]', { month, year });
      const response = await this.request(`/finance/balance?month=${month}&year=${year}`);
      log('✅ [getMonthlyBalance] finalBalance:', response?.finalBalance);
      return response;
    } catch (error) {
      logError('❌ [getMonthlyBalance] Error:', error.message);
      throw error;
    }
  }

  /**
   * Indicador en tiempo real de salud financiera del mes.
   * Devuelve semáforo (HEALTHY / WARNING / CRITICAL), barra de cobertura y proyecciones.
   * GET /finance/health?month={month}&year={year}
   * @param {number} month - 1-12
   * @param {number} year  - ≥ 2000
   * @returns {FinancialHealthDTO}
   */
  async getFinancialHealthIndicator(month, year) {
    try {
      validateNumber(month, 'month', 1, 12);
      validateNumber(year, 'year', 2000);
      log('❤️ [getFinancialHealthIndicator]', { month, year });
      const response = await this.request(`/finance/health?month=${month}&year=${year}`);
      log('✅ [getFinancialHealthIndicator] status:', response?.status);
      return response;
    } catch (error) {
      logError('❌ [getFinancialHealthIndicator] Error:', error.message);
      throw error;
    }
  }

  /**
   * Cerrar el mes.
   * Congela el balance; el saldo final se arrastra automáticamente al mes siguiente.
   * POST /finance/balance/close?month={month}&year={year}&closedBy={closedBy}&notes={notes}
   * @param {number} month
   * @param {number} year
   * @param {string} closedBy - username del usuario que cierra
   * @param {string|null} notes - notas opcionales
   * @returns {MonthlyBalanceDTO} balance cerrado
   */
  async closeMonth(month, year, closedBy, notes = null) {
    try {
      validateNumber(month, 'month', 1, 12);
      validateNumber(year, 'year', 2000);
      validateString(closedBy, 'closedBy', 1, 100);

      const params = new URLSearchParams();
      params.append('month', month);
      params.append('year', year);
      params.append('closedBy', closedBy.trim());
      if (notes) params.append('notes', notes.trim());

      log('🔒 [closeMonth] Cerrando mes', { month, year, closedBy });
      const response = await this.request(`/finance/balance/close?${params.toString()}`, {
        method: 'POST',
      });
      log('✅ [closeMonth] Mes cerrado - finalBalance:', response?.finalBalance);
      return response;
    } catch (error) {
      logError('❌ [closeMonth] Error:', error.message);
      throw error;
    }
  }

  // ========== 📄 INFORMES PDF ==========

  /**
   * Descargar informe PDF mensual.
   * GET /finance/reports/monthly?month={month}&year={year}
   * Descarga automáticamente el archivo en el navegador.
   * @param {number} month
   * @param {number} year
   */
  async downloadMonthlyReport(month, year) {
    try {
      validateNumber(month, 'month', 1, 12);
      validateNumber(year, 'year', 2000);

      log('📄 [downloadMonthlyReport]', { month, year });
      const token = sessionStorage.getItem('token');
      const url = `${API_BASE_URL}/finance/reports/monthly?month=${month}&year=${year}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('authTokenExpired'));
        throw new Error('Sesión expirada');
      }
      if (!res.ok) throw new Error(`Error generando PDF mensual: ${res.status}`);

      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `informe-mensual-${month}-${year}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);

      log('✅ [downloadMonthlyReport] Descarga iniciada');
    } catch (error) {
      logError('❌ [downloadMonthlyReport] Error:', error.message);
      throw error;
    }
  }

  /**
   * Descargar informe PDF anual con comparativo mes a mes.
   * GET /finance/reports/yearly?year={year}
   * @param {number} year
   */
  async downloadYearlyReport(year) {
    try {
      validateNumber(year, 'year', 2000);

      log('📅 [downloadYearlyReport]', { year });
      const token = sessionStorage.getItem('token');
      const url = `${API_BASE_URL}/finance/reports/yearly?year=${year}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('authTokenExpired'));
        throw new Error('Sesión expirada');
      }
      if (!res.ok) throw new Error(`Error generando PDF anual: ${res.status}`);

      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `informe-anual-${year}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);

      log('✅ [downloadYearlyReport] Descarga iniciada');
    } catch (error) {
      logError('❌ [downloadYearlyReport] Error:', error.message);
      throw error;
    }
  }

  /**
   * Descargar informe PDF por rango de fechas.
   * GET /finance/reports/period?startDate={YYYY-MM-DD}&endDate={YYYY-MM-DD}
   * @param {string} startDate - formato YYYY-MM-DD
   * @param {string} endDate   - formato YYYY-MM-DD
   */
  async downloadPeriodReport(startDate, endDate) {
    try {
      validateString(startDate, 'startDate', 10, 10);
      validateString(endDate, 'endDate', 10, 10);

      log('📆 [downloadPeriodReport]', { startDate, endDate });
      const token = sessionStorage.getItem('token');
      const url = `${API_BASE_URL}/finance/reports/period?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('authTokenExpired'));
        throw new Error('Sesión expirada');
      }
      if (!res.ok) throw new Error(`Error generando PDF por período: ${res.status}`);

      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `informe-periodo-${startDate}-${endDate}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);

      log('✅ [downloadPeriodReport] Descarga iniciada');
    } catch (error) {
      logError('❌ [downloadPeriodReport] Error:', error.message);
      throw error;
    }
  }

  // ============================================================
  // 🕊️ MÓDULO DE CONSEJERÍAS PASTORALES
  // Base URL del módulo: /api/v1/counseling
  // ============================================================

  // ========== 🕊️ CONSEJERÍAS ==========

  /**
   * Agendar una nueva sesión de consejería.
   * Envía notificación Telegram al pastor y al miembro automáticamente.
   * POST /counseling
   * @param {Object} dto - CounselingSessionRequest
   *   { memberId, scheduledAt (yyyy-MM-dd'T'HH:mm), durationMinutes, location, topic, objectives }
   */
  async scheduleSession(dto) {
    try {
      if (!dto || typeof dto !== 'object') throw new Error('Datos de sesión inválidos');
      validateId(dto.memberId, 'memberId');
      validateString(dto.scheduledAt, 'scheduledAt', 1, 20);

      log('📅 [scheduleSession] Agendando sesión para miembro:', dto.memberId);
      const response = await this.request('/counseling', {
        method: 'POST',
        body: JSON.stringify(dto),
      });
      log('✅ [scheduleSession] Éxito - ID:', response?.id);
      return response;
    } catch (error) {
      logError('❌ [scheduleSession] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener lista simplificada de pastores activos en el sistema.
   * GET /auth/users/pastors
   * Retorna: PastorResponse[]
   */
  async getPastors() {
    try {
      log('📋 [getPastors] Obteniendo pastores activos');
      const response = await this.request('/auth/users/pastors');
      return response;
    } catch (error) {
      logError('❌ [getPastors] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener agenda administrativa de consejerías de forma segura (paginado).
   * GET /counseling/schedule?page={page}&size={size}
   * Retorna: Page<CounselingScheduleResponse>
   */
  async getCounselingSchedule(page = 0, size = 20) {
    try {
      log(`📋 [getCounselingSchedule] Obteniendo agenda (pág: ${page}, tamaño: ${size})`);
      const response = await this.request(`/counseling/schedule?page=${page}&size=${size}`);
      return response;
    } catch (error) {
      logError('❌ [getCounselingSchedule] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener detalle seguro de la agenda para una sesión específica.
   * GET /counseling/schedule/{id}
   * Retorna: CounselingScheduleResponse
   */
  async getCounselingScheduleById(id) {
    try {
      validateId(id, 'sessionId');
      log('🔍 [getCounselingScheduleById] Buscando detalle seguro ID:', id);
      const response = await this.request(`/counseling/schedule/${id}`);
      return response;
    } catch (error) {
      logError('❌ [getCounselingScheduleById] Error:', error.message);
      throw error;
    }
  }

  /**
   * Listar todas las sesiones del pastor autenticado.
   * GET /counseling
   * Retorna: CounselingSessionResponse[]
   */
  async getMySessions() {
    try {
      log('📋 [getMySessions] Obteniendo sesiones del pastor');
      const response = await this.request('/counseling');
      log('✅ [getMySessions] Éxito -', response?.length || 0, 'sesiones');
      return response;
    } catch (error) {
      logError('❌ [getMySessions] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener el detalle de una sesión específica.
   * El backend valida que la sesión pertenezca al pastor autenticado.
   * GET /counseling/{id}
   * @param {number} id - ID de la sesión
   */
  async getCounselingSessionById(id) {
    try {
      validateId(id, 'sessionId');
      log('🔍 [getCounselingSessionById] Buscando sesión ID:', id);
      const response = await this.request(`/counseling/${id}`);
      return response;
    } catch (error) {
      logError('❌ [getCounselingSessionById] Error:', error.message);
      throw error;
    }
  }

  /**
   * Filtrar sesiones del pastor autenticado por estado.
   * GET /counseling/status/{status}
   * @param {string} status - SCHEDULED | COMPLETED | CANCELLED | NO_SHOW | RESCHEDULED
   */
  async getSessionsByStatus(status) {
    try {
      validateString(status, 'status', 1, 30);
      const validStatuses = ['SCHEDULED', 'COMPLETED', 'CANCELLED', 'NO_SHOW', 'RESCHEDULED'];
      if (!validStatuses.includes(status)) {
        throw new Error(`Estado inválido. Debe ser uno de: ${validStatuses.join(', ')}`);
      }
      log('🔍 [getSessionsByStatus] Filtrando por estado:', status);
      const response = await this.request(`/counseling/status/${status}`);
      log('✅ [getSessionsByStatus] Éxito -', response?.length || 0, 'sesiones');
      return response;
    } catch (error) {
      logError('❌ [getSessionsByStatus] Error:', error.message);
      throw error;
    }
  }

  /**
   * Historial completo de sesiones de un miembro gestionadas por el pastor autenticado.
   * GET /counseling/member/{memberId}/history
   * @param {number} memberId
   */
  async getMemberCounselingHistory(memberId) {
    try {
      validateId(memberId, 'memberId');
      log('📋 [getMemberCounselingHistory] Historial del miembro ID:', memberId);
      const response = await this.request(`/counseling/member/${memberId}/history`);
      log('✅ [getMemberCounselingHistory] Éxito -', response?.length || 0, 'sesiones');
      return response;
    } catch (error) {
      logError('❌ [getMemberCounselingHistory] Error:', error.message);
      throw error;
    }
  }

  /**
   * Actualizar los datos básicos de una sesión SCHEDULED o RESCHEDULED.
   * Si cambia el horario, el backend reinicia los flags de recordatorio.
   * PUT /counseling/{id}
   * @param {number} id - ID de la sesión
   * @param {Object} dto - CounselingSessionRequest (mismos campos que scheduleSession)
   */
  async updateCounselingSession(id, dto) {
    try {
      validateId(id, 'sessionId');
      if (!dto || typeof dto !== 'object') throw new Error('Datos de sesión inválidos');
      validateId(dto.memberId, 'memberId');

      log('📝 [updateCounselingSession] Actualizando sesión ID:', id);
      const response = await this.request(`/counseling/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dto),
      });
      log('✅ [updateCounselingSession] Éxito');
      return response;
    } catch (error) {
      logError('❌ [updateCounselingSession] Error:', error.message);
      throw error;
    }
  }

  /**
   * Marcar la sesión como COMPLETED y registrar las notas del pastor.
   * PATCH /counseling/{id}/complete
   * @param {number} id - ID de la sesión
   * @param {Object} dto - CompleteSessionRequest
   *   { notes, followUpRequired, followUpNotes, followUpDate (yyyy-MM-dd'T'HH:mm) }
   */
  async completeSession(id, dto) {
    try {
      validateId(id, 'sessionId');
      if (!dto || typeof dto !== 'object') throw new Error('Datos de completado inválidos');
      if (!dto.notes || !dto.notes.trim()) throw new Error('Las notas son obligatorias para completar la sesión');

      log('✅ [completeSession] Completando sesión ID:', id);
      const response = await this.request(`/counseling/${id}/complete`, {
        method: 'PATCH',
        body: JSON.stringify(dto),
      });
      log('✅ [completeSession] Éxito');
      return response;
    } catch (error) {
      logError('❌ [completeSession] Error:', error.message);
      throw error;
    }
  }

  /**
   * Cancelar la sesión indicando el motivo.
   * PATCH /counseling/{id}/cancel
   * @param {number} id - ID de la sesión
   * @param {Object} dto - CancelSessionRequest { cancellationReason }
   */
  async cancelSession(id, dto) {
    try {
      validateId(id, 'sessionId');
      if (!dto || typeof dto !== 'object') throw new Error('Datos de cancelación inválidos');
      validateString(dto.cancellationReason, 'cancellationReason', 3, 500);

      log('❌ [cancelSession] Cancelando sesión ID:', id);
      const response = await this.request(`/counseling/${id}/cancel`, {
        method: 'PATCH',
        body: JSON.stringify(dto),
      });
      log('✅ [cancelSession] Éxito');
      return response;
    } catch (error) {
      logError('❌ [cancelSession] Error:', error.message);
      throw error;
    }
  }

  /**
   * Marcar el miembro como NO_SHOW (no asistió a la sesión programada).
   * PATCH /counseling/{id}/no-show
   * @param {number} id - ID de la sesión
   */
  async markSessionNoShow(id) {
    try {
      validateId(id, 'sessionId');
      log('👻 [markSessionNoShow] Marcando no-show sesión ID:', id);
      const response = await this.request(`/counseling/${id}/no-show`, {
        method: 'PATCH',
      });
      log('✅ [markSessionNoShow] Éxito');
      return response;
    } catch (error) {
      logError('❌ [markSessionNoShow] Error:', error.message);
      throw error;
    }
  }

  /**
   * Inicia una sesión en tiempo real (SCHEDULED/RESCHEDULED → IN_PROGRESS).
   * Devuelve StartSessionResponse con la sesión activa + contexto histórico completo del miembro.
   *
   * PATCH /counseling/{id}/start
   * @param {number} id - ID de la sesión a iniciar
   * @returns {StartSessionResponse}
   */
  async startCounselingSession(id) {
    try {
      validateId(id, 'sessionId');
      log('▶️ [startCounselingSession] Iniciando sesión ID:', id);
      const response = await this.request(`/counseling/${id}/start`, {
        method: 'PATCH',
      });
      log('✅ [startCounselingSession] Sesión en curso -',
        response?.activeSession?.id,
        '| Sesiones previas:', response?.totalPreviousSessions || 0,
        '| Follow-ups pendientes:', response?.pendingFollowUps?.length || 0
      );
      return response;
    } catch (error) {
      logError('❌ [startCounselingSession] Error:', error.message);
      throw error;
    }
  }

  // ========== 📄 INFORMES PDF DE CONSEJERÍAS ==========

  /**
   * Genera y descarga el PDF con el historial de consejerías de un miembro.
   * GET /counseling/report/member/{memberId}
   * @param {number} memberId
   * @param {string} memberName - Nombre del miembro (solo para el nombre del archivo)
   */
  async downloadCounselingMemberHistoryPdf(memberId, memberName = '') {
    try {
      validateId(memberId, 'memberId');

      log('📄 [downloadCounselingMemberHistoryPdf] Generando PDF historial miembro:', memberId);
      const token = sessionStorage.getItem('token');
      const url = `${API_BASE_URL}/counseling/report/member/${memberId}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('authTokenExpired'));
        throw new Error('Sesión expirada');
      }
      if (!res.ok) throw new Error(`Error generando PDF historial: ${res.status}`);

      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const safeName = memberName.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '') || memberId;
      link.download = `historial-consejeria-${safeName}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);

      log('✅ [downloadCounselingMemberHistoryPdf] Descarga iniciada');
    } catch (error) {
      logError('❌ [downloadCounselingMemberHistoryPdf] Error:', error.message);
      throw error;
    }
  }

  /**
   * Genera y descarga el PDF con el informe de gestión del pastor (sesiones por mes/año).
   * GET /counseling/report/management?year={year}
   * @param {number} year - Año del informe (default: año actual)
   */
  async downloadCounselingManagementReportPdf(year = null) {
    try {
      const reportYear = year || new Date().getFullYear();
      validateNumber(reportYear, 'year', 2000);

      log('📊 [downloadCounselingManagementReportPdf] Generando informe gestión año:', reportYear);
      const token = sessionStorage.getItem('token');
      const url = `${API_BASE_URL}/counseling/report/management?year=${reportYear}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('authTokenExpired'));
        throw new Error('Sesión expirada');
      }
      if (!res.ok) throw new Error(`Error generando PDF de gestión: ${res.status}`);

      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `informe-gestion-consejerias-${reportYear}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);

      log('✅ [downloadCounselingManagementReportPdf] Descarga iniciada');
    } catch (error) {
      logError('❌ [downloadCounselingManagementReportPdf] Error:', error.message);
      throw error;
    }
  }

  // ========== 🔔 RECORDATORIOS DE CONSEJERÍAS (admin/test) ==========

  /**
   * Fuerza el envío manual de recordatorios D-1 (día anterior).
   * Útil para pruebas o recuperación de fallos del scheduler.
   * POST /counseling/reminders/trigger-day-before
   * Solo PASTORES.
   */
  async triggerCounselingDayBeforeReminders() {
    try {
      log('🔔 [triggerCounselingDayBeforeReminders] Forzando recordatorios D-1');
      const response = await this.request('/counseling/reminders/trigger-day-before', {
        method: 'POST',
      });
      log('✅ [triggerCounselingDayBeforeReminders] Éxito');
      return response;
    } catch (error) {
      logError('❌ [triggerCounselingDayBeforeReminders] Error:', error.message);
      throw error;
    }
  }

  /**
   * Fuerza el envío manual de recordatorios D-0 (día de la sesión).
   * POST /counseling/reminders/trigger-day-of
   * Solo PASTORES.
   */
  async triggerCounselingDayOfReminders() {
    try {
      log('🔔 [triggerCounselingDayOfReminders] Forzando recordatorios D-0');
      const response = await this.request('/counseling/reminders/trigger-day-of', {
        method: 'POST',
      });
      log('✅ [triggerCounselingDayOfReminders] Éxito');
      return response;
    } catch (error) {
      logError('❌ [triggerCounselingDayOfReminders] Error:', error.message);
      throw error;
    }
  }

  // ========== 🎓 ACTIVIDADES ENROLLMENT DISPONIBLES ==========

  /**
   * Devuelve las actividades ENROLLMENT activas y no vencidas para las que
   * un miembro es elegible según su nivel actual (currentLevel).
   *
   * Solo incluye actividades cuyo nivel tiene requiresPayment = true y para
   * las que el miembro cumple la regla de nivel previo:
   *   - Primer nivel (isFirst) → acceso abierto
   *   - Cualquier otro         → currentLevel.levelOrder == requiredLevel.levelOrder - 1
   *
   * GET /api/v1/activity/enrollment/available/{memberId}
   *
   * Respuesta: AvailableEnrollmentActivityDTO[]
   * {
   *   activityId, activityName, price, endDate,
   *   requiredLevelId, requiredLevelCode, requiredLevelDisplayName, requiredLevelOrder,
   *   linkedEnrollmentId, cohortName,
   *   openAccess,        ← true si es el primer nivel (sin requisito previo)
   *   eligibilityReason  ← mensaje legible para mostrar al miembro
   * }
   *
   * @param {number} memberId - ID del miembro
   */
  async getAvailableEnrollmentActivities(memberId) {
    try {
      validateId(memberId, "memberId");
      log("🎓 [getAvailableEnrollmentActivities] Miembro ID:", memberId);
      const response = await this.request(
        `/activity/enrollment/available/${memberId}`
      );
      log(
        "✅ [getAvailableEnrollmentActivities] Éxito -",
        response?.length || 0,
        "actividades disponibles"
      );
      return response || [];
    } catch (error) {
      logError("❌ [getAvailableEnrollmentActivities] Error:", error.message);
      throw error;
    }
  }

  // ========== 🎯 CONTRIBUCIONES DE ACTIVIDAD ==========

  async enrollInActivity(memberId, activityId, initialPayment, incomeMethod, quantity) {
    try {
      const currentUser = this.getCurrentUser();
      return await this.request('/activity-contribution/create-with-initial-payment', {
        method: 'POST',
        body: JSON.stringify({
          memberId,
          activityId,
          incomeMethod: incomeMethod || 'CASH',
          recordedBy: currentUser?.username || 'Sistema',
          initialPaymentAmount: initialPayment > 0 ? initialPayment : null,
          quantity: quantity || 1,
        }),
      });
    } catch (error) {
      console.error('❌ [enrollInActivity] Error:', error.message);
      throw error;
    }
  }

  // En apiService.js, dentro del bloque de CONTRIBUCIONES DE ACTIVIDAD
  async deleteActivityContribution(contributionId) {
    try {
      validateId(contributionId, 'contributionId');
      log('🗑️ [deleteActivityContribution] Eliminando contribución ID:', contributionId);
      const response = await this.request(
        `/activity-contribution/delete/${contributionId}`,
        { method: 'DELETE' }
      );
      log('✅ [deleteActivityContribution] Eliminado correctamente');
      return response;
    } catch (error) {
      logError('❌ [deleteActivityContribution] Error:', error.message);
      throw error;
    }
  }

  // ========== ⚙️ NIVELES — ACTUALIZAR requiresPayment ==========

  /**
   * Actualiza los campos de un nivel formativo.
   * Permite cambiar requiresPayment sin tocar código ni migraciones.
   *
   * PUT /api/v1/levels/{id}
   * Body (todos opcionales):
   *   { displayName, description, levelOrder, isActive, requiresPayment }
   *
   * Requiere rol: PASTORES
   *
   * @param {number} levelId   - ID del nivel a actualizar
   * @param {Object} updates   - Campos a cambiar
   * @param {boolean} [updates.requiresPayment] - true/false
   * @param {string}  [updates.displayName]
   * @param {string}  [updates.description]
   * @param {number}  [updates.levelOrder]
   * @param {boolean} [updates.isActive]
   */
  async updateLevel(levelId, updates) {
    try {
      validateId(levelId, "levelId");
      if (!updates || typeof updates !== "object") {
        throw new Error("Datos de actualización inválidos");
      }

      log("⚙️ [updateLevel] Actualizando nivel ID:", levelId, updates);
      const response = await this.request(`/levels/${levelId}`, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      log(
        "✅ [updateLevel] Nivel actualizado:",
        response?.code,
        "requiresPayment:",
        response?.requiresPayment
      );
      return response;
    } catch (error) {
      logError("❌ [updateLevel] Error:", error.message);
      throw error;
    }
  }

  /**
   * Obtiene notificaciones activas por username (NO requiere ID)
   */
  getActiveNotificationsByUsername(username) {
    return this.request(`/notifications/user/by-username/${username}/active`, {
      method: 'GET',
      requiresAuth: true
    });
  }

  /**
   * Obtiene conteo de notificaciones sin leer por username
   */
  getUnreadNotificationCountByUsername(username) {
    return this.request(`/notifications/user/by-username/${username}/unread-count`, {
      method: 'GET',
      requiresAuth: true
    }).then(data => data.unreadCount);
  }

  // ========== ⚙️ NIVELES FORMATIVOS — CRUD COMPLETO ==========

  /**
   * Crear nuevo nivel formativo.
   * POST /api/v1/levels
   * Requiere rol: PASTORES
   * @param {Object} levelData - { code, displayName, description, levelOrder, isActive, requiresPayment }
   */
  async createLevel(levelData) {
    try {
      if (!levelData || typeof levelData !== 'object') {
        throw new Error('Datos de nivel inválidos');
      }
      log('✅ [createLevel] Creando nivel:', levelData.code);
      const response = await this.request('/levels', {
        method: 'POST',
        body: JSON.stringify(levelData),
      });
      log('✅ [createLevel] Éxito - ID:', response?.id);
      return response;
    } catch (error) {
      logError('❌ [createLevel] Error:', error.message);
      throw error;
    }
  }

  /**
   * Desactivar nivel (borrado lógico).
   * DELETE /api/v1/levels/{id}
   * Requiere rol: PASTORES
   * @param {number} id
   */
  async deactivateLevel(id) {
    try {
      validateId(id, 'levelId');
      log('🚫 [deactivateLevel] Desactivando nivel ID:', id);
      const response = await this.request(`/levels/${id}`, {
        method: 'DELETE',
      });
      log('✅ [deactivateLevel] Éxito');
      return response;
    } catch (error) {
      logError('❌ [deactivateLevel] Error:', error.message);
      throw error;
    }
  }

  // ========== 📖 LECCIONES PLANTILLA — CRUD COMPLETO ==========

  /**
   * Listar todas las plantillas de lección de un nivel (activas e inactivas).
   * GET /api/v1/lesson-templates/level/{levelCode}
   * @param {string} levelCode - Código del nivel (ej: ESENCIA_1)
   */
  async getLessonTemplatesByLevel(levelCode) {
    try {
      validateString(levelCode, 'levelCode', 1, 50);
      log('📖 [getLessonTemplatesByLevel] Nivel:', levelCode);
      const response = await this.request(`/lesson-templates/level/${levelCode.toUpperCase()}`);
      log('✅ [getLessonTemplatesByLevel] Éxito -', response?.length || 0, 'plantillas');
      return response;
    } catch (error) {
      logError('❌ [getLessonTemplatesByLevel] Error:', error.message);
      throw error;
    }
  }

  /**
   * Crear nueva plantilla de lección.
   * POST /api/v1/lesson-templates
   * Requiere rol: PASTORES
   * Body: { level: { id }, lessonName, lessonOrder, defaultDurationMinutes }
   */
  async createLessonTemplate(templateData) {
    try {
      if (!templateData || typeof templateData !== 'object') {
        throw new Error('Datos de plantilla inválidos');
      }
      log('✅ [createLessonTemplate] Creando:', templateData.lessonName);
      const response = await this.request('/lesson-templates', {
        method: 'POST',
        body: JSON.stringify(templateData),
      });
      log('✅ [createLessonTemplate] Éxito - ID:', response?.id);
      return response;
    } catch (error) {
      logError('❌ [createLessonTemplate] Error:', error.message);
      throw error;
    }
  }

  /**
   * Actualizar plantilla de lección.
   * PUT /api/v1/lesson-templates/{id}
   * Requiere rol: PASTORES
   * @param {number} id
   * @param {Object} updates - { lessonName, lessonOrder, defaultDurationMinutes, isActive }
   */
  async updateLessonTemplate(id, updates) {
    try {
      validateId(id, 'lessonTemplateId');
      if (!updates || typeof updates !== 'object') {
        throw new Error('Datos de actualización inválidos');
      }
      log('📝 [updateLessonTemplate] Actualizando ID:', id);
      const response = await this.request(`/lesson-templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      log('✅ [updateLessonTemplate] Éxito');
      return response;
    } catch (error) {
      logError('❌ [updateLessonTemplate] Error:', error.message);
      throw error;
    }
  }

  /**
   * Desactivar plantilla de lección (borrado lógico).
   * DELETE /api/v1/lesson-templates/{id}
   * Requiere rol: PASTORES
   * @param {number} id
   */
  async deactivateLessonTemplate(id) {
    try {
      validateId(id, 'lessonTemplateId');
      log('🚫 [deactivateLessonTemplate] Desactivando ID:', id);
      const response = await this.request(`/lesson-templates/${id}`, {
        method: 'DELETE',
      });
      log('✅ [deactivateLessonTemplate] Éxito');
      return response;
    } catch (error) {
      logError('❌ [deactivateLessonTemplate] Error:', error.message);
      throw error;
    }
  }

  // ============================================================
  // 🎸 MÓDULO DE ALABANZA (WORSHIP)
  // Base URL del módulo: /api/v1/worship
  // ============================================================

  /**
   * Obtener todos los integrantes del equipo de alabanza.
   * GET /api/v1/worship
   */
  async getWorshipTeam() {
    try {
      log('🎸 [getWorshipTeam] Obteniendo equipo de alabanza');
      const response = await this.request('/worship');
      log('✅ [getWorshipTeam] Éxito -', response?.length || 0, 'adoradores');
      return response;
    } catch (error) {
      logError('❌ [getWorshipTeam] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener la lista de instrumentos/roles configurados en el sistema.
   * GET /api/v1/worship/roles
   */
  async getWorshipRoles() {
    try {
      log('🎹 [getWorshipRoles] Obteniendo instrumentos configurados');
      const response = await this.request('/worship/roles');
      return response;
    } catch (error) {
      logError('❌ [getWorshipRoles] Error:', error.message);
      throw error;
    }
  }

  /**
   * Buscar músicos disponibles (espiritual y técnicamente) para un instrumento específico.
   * GET /api/v1/worship/available/{roleId}
   * @param {number} roleId - ID del instrumento/rol
   */
  async getAvailableWorshipMembers(roleId) {
    try {
      validateId(roleId, 'roleId');
      log('🔍 [getAvailableWorshipMembers] Buscando disponibles para rol ID:', roleId);
      return this.request(`/worship/available/${roleId}`);
    } catch (error) {
      logError('❌ [getAvailableWorshipMembers] Error:', error.message);
      throw error;
    }
  }

  /**
   * Añadir un líder activo al equipo de alabanza.
   * POST /api/v1/worship/add
   * @param {number} memberId - ID del miembro (debe ser líder activo)
   * @param {number} primaryRoleId - ID del instrumento principal
   * @param {Array<number>} skillsIds - Array con IDs de instrumentos secundarios
   * @param {string} notes - Notas opcionales
   */
  async addWorshipMember(memberId, primaryRoleId, skillsIds = [], notes = null) {
    try {
      validateId(memberId, 'memberId');
      validateId(primaryRoleId, 'primaryRoleId');

      const params = new URLSearchParams();
      params.append('memberId', memberId);

      // 🚀 CORRECCIÓN AQUÍ: El backend espera 'primaryRoleId', no 'roleId'
      params.append('primaryRoleId', primaryRoleId);

      if (skillsIds && skillsIds.length > 0) {
        params.append('skills', skillsIds.join(','));
      }
      if (notes) {
        params.append('notes', notes.trim());
      }

      log('➕ [addWorshipMember] Añadiendo miembro al equipo:', memberId);
      const response = await this.request(`/worship/add?${params.toString()}`, {
        method: 'POST'
      });
      log('✅ [addWorshipMember] Éxito');
      return response;
    } catch (error) {
      logError('❌ [addWorshipMember] Error:', error.message);
      throw error;
    }
  }

  /**
   * Actualizar el estado técnico/musical de un adorador.
   * PUT /api/v1/worship/{id}/status
   * @param {number} worshipId - ID del registro en worship_team_member
   * @param {string} status - ACTIVE, SUSPENDED, INACTIVE
   * @param {string} notes - Motivo del cambio de estado
   */
  /**
   * Actualizar el perfil completo de un adorador (Instrumentos y Estado).
   * PUT /api/v1/worship/{id}
   */
  async updateWorshipMemberProfile(worshipId, primaryRoleId, skillsIds = [], status, notes = null) {
    try {
      validateId(worshipId, 'worshipId');
      validateId(primaryRoleId, 'primaryRoleId');
      validateString(status, 'status', 1, 20);

      const params = new URLSearchParams();
      params.append('primaryRoleId', primaryRoleId);
      if (skillsIds && skillsIds.length > 0) {
        params.append('skills', skillsIds.join(','));
      }
      params.append('status', status);
      if (notes) {
        params.append('notes', notes.trim());
      }

      log('🔄 [updateWorshipMemberProfile] Actualizando adorador ID:', worshipId);
      const response = await this.request(`/worship/${worshipId}?${params.toString()}`, {
        method: 'PUT'
      });
      log('✅ [updateWorshipMemberProfile] Éxito');
      return response;
    } catch (error) {
      logError('❌ [updateWorshipMemberProfile] Error:', error.message);
      throw error;
    }
  }

  // ========== 📅 SCHEDULING (PROGRAMACIÓN DE ALABANZA) ==========

  /**
   * Crear un nuevo evento o culto en el calendario.
   * POST /api/v1/worship/schedule/event
   * @param {string} name - Nombre (Ej: Culto Dominical)
   * @param {string} type - EventType (CULTO_DOMINGO, REUNION_JOVENES, etc)
   * @param {string} date - Fecha y hora ISO (Ej: 2026-08-15T09:00:00)
   * @param {string} description - Detalles adicionales
   * @param {number} praiseSongCount - Cantidad de alabanzas requeridas
   * @param {number} worshipSongCount - Cantidad de adoración requerida
   */
  async createWorshipEvent(name, type, date, description = null, praiseSongCount = 0, worshipSongCount = 0) {
    try {
      validateString(name, 'name', 3, 100);
      validateString(type, 'type', 1, 50);
      validateString(date, 'date', 10, 30);

      const params = new URLSearchParams();
      params.append('name', name.trim());
      params.append('type', type);
      params.append('date', date);
      params.append('praiseSongCount', praiseSongCount); // ✅ Agregado
      params.append('worshipSongCount', worshipSongCount); // ✅ Agregado
      if (description) params.append('description', description.trim());

      log('📅 [createWorshipEvent] Creando evento:', name);
      return await this.request(`/worship/schedule/event?${params.toString()}`, { method: 'POST' });
    } catch (error) {
      logError('❌ [createWorshipEvent] Error:', error.message);
      throw error;
    }
  }

  /**
   * Registrar la asistencia del equipo de alabanza a un evento.
   * POST /api/v1/worship/schedule/event/{eventId}/attendance
   */
  async recordWorshipAttendance(eventId, attendanceList) {
    try {
      validateId(eventId, 'eventId');
      if (!Array.isArray(attendanceList)) throw new Error('Los datos de asistencia son inválidos');

      log('📋 [recordWorshipAttendance] Registrando asistencia evento ID:', eventId);
      const response = await this.request(`/worship/schedule/event/${eventId}/attendance`, {
        method: 'POST',
        body: JSON.stringify(attendanceList)
      });
      log('✅ [recordWorshipAttendance] Éxito');
      return response;
    } catch (error) {
      logError('❌ [recordWorshipAttendance] Error:', error.message);
      throw error;
    }
  }

  /**
   * Asignar a todos los miembros activos a un evento específico.
   * POST /api/v1/worship/schedule/event/{id}/assign-all
   */
  async assignAllWorshipMembersToEvent(eventId) {
    try {
      validateId(eventId, 'eventId');
      log('👥 [assignAllWorshipMembersToEvent] Asignando todos al evento:', eventId);
      const response = await this.request(`/worship/schedule/event/${eventId}/assign-all`, {
        method: 'POST'
      });
      log('✅ [assignAllWorshipMembersToEvent] Éxito');
      return response;
    } catch (error) {
      logError('❌ [assignAllWorshipMembersToEvent] Error:', error.message);
      throw error;
    }
  }

  /**
   * Actualizar un evento/culto en el calendario.
   * PUT /api/v1/worship/schedule/event/{id}
   */
  async updateWorshipEvent(id, name, type, date, description = null, praiseSongCount = 0, worshipSongCount = 0) {
    try {
      validateId(id, 'eventId');
      validateString(name, 'name', 3, 100);
      const params = new URLSearchParams();
      params.append('name', name.trim());
      params.append('type', type);
      params.append('date', date);
      params.append('praiseSongCount', praiseSongCount); // ✅ Agregado
      params.append('worshipSongCount', worshipSongCount); // ✅ Agregado
      if (description) params.append('description', description.trim());

      log('📅 [updateWorshipEvent] Actualizando evento ID:', id);
      return await this.request(`/worship/schedule/event/${id}?${params.toString()}`, { method: 'PUT' });
    } catch (error) {
      logError('❌ [updateWorshipEvent] Error:', error.message);
      throw error;
    }
  }

  /**
   * Sincronizar las asignaciones editadas de un evento.
   * PUT /api/v1/worship/schedule/event/{eventId}/assignments
   */
  async syncEventAssignments(eventId, assignmentsList) {
    try {
      validateId(eventId, 'eventId');
      if (!Array.isArray(assignmentsList)) throw new Error('Las asignaciones deben ser un arreglo');

      log('🔄 [syncEventAssignments] Sincronizando músicos para evento ID:', eventId);
      return await this.request(`/worship/schedule/event/${eventId}/assignments`, {
        method: 'PUT',
        body: JSON.stringify(assignmentsList) // Aquí el componente ya envía { roleId, memberId }
      });
    } catch (error) {
      logError('❌ [syncEventAssignments] Error:', error.message);
      throw error;
    }
  }

  /**
   * Eliminar un evento/culto en el calendario.
   * DELETE /api/v1/worship/schedule/event/{id}
   */
  async deleteWorshipEvent(id) {
    try {
      validateId(id, 'eventId');
      log('🗑️ [deleteWorshipEvent] Eliminando evento ID:', id);
      const response = await this.request(`/worship/schedule/event/${id}`, {
        method: 'DELETE'
      });
      log('✅ [deleteWorshipEvent] Éxito');
      return response;
    } catch (error) {
      logError('❌ [deleteWorshipEvent] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtener todos los eventos y sus asignaciones.
   * GET /api/v1/worship/schedule/events
   */
  async getWorshipEvents() {
    try {
      log('📅 [getWorshipEvents] Obteniendo calendario de eventos');
      return await this.request('/worship/schedule/events') || [];
    } catch (error) {
      logError('❌ [getWorshipEvents] Error:', error.message);
      return [];
    }
  }

  /**
   * Asignar manualmente a un músico a un evento.
   * POST /api/v1/worship/schedule/assign
   */
  async assignWorshipMember(eventId, worshipMemberId, roleId) {
    try {
      validateId(eventId, 'eventId');
      validateId(worshipMemberId, 'worshipMemberId');
      validateId(roleId, 'roleId');

      const params = new URLSearchParams();
      params.append('eventId', eventId);
      params.append('worshipMemberId', worshipMemberId);
      params.append('roleId', roleId); // ID del instrumento a tocar

      log('📌 [assignWorshipMember] Asignando adorador:', worshipMemberId, 'al evento:', eventId);
      const response = await this.request(`/worship/schedule/assign?${params.toString()}`, {
        method: 'POST'
      });
      log('✅ [assignWorshipMember] Éxito');
      return response;
    } catch (error) {
      logError('❌ [assignWorshipMember] Error:', error.message);
      throw error;
    }
  }

  /**
    * Ejecutar el algoritmo de auto-sugerencia mensual equitativa.
    * POST /api/v1/worship/schedule/auto-suggest/batch
    * @param {Object} data - MonthlyScheduleRequest { eventIds: [1,2,3], requiredRoles: { "1": 1, "2": 3 }, praiseSongCount: 2, worshipSongCount: 2 }
    */
  async autoSuggestWorshipSchedule(data) {
    try {
      if (!data || !Array.isArray(data.eventIds)) throw new Error('Datos inválidos');
      log('✨ [autoSuggestWorshipSchedule] Procesando batch');
      return await this.request('/worship/schedule/auto-suggest/batch', {
        method: 'POST',
        body: JSON.stringify(data) // ✅ Incluye eventIds, requiredRoles, praiseSongCount y worshipSongCount
      });
    } catch (error) {
      logError('❌ [autoSuggestWorshipSchedule] Error:', error.message);
      throw error;
    }
  }
  /**
   * Crear un nuevo instrumento/rol de alabanza.
   * POST /api/v1/worship/roles
   */
  async createWorshipRole(roleData) {
    try {
      if (!roleData || typeof roleData !== 'object') throw new Error('Datos de rol inválidos');
      validateString(roleData.name, 'name', 2, 50);

      log('🎹 [createWorshipRole] Creando rol:', roleData.name);
      const response = await this.request('/worship/roles', {
        method: 'POST',
        body: JSON.stringify(roleData)
      });
      log('✅ [createWorshipRole] Éxito');
      return response;
    } catch (error) {
      logError('❌ [createWorshipRole] Error:', error.message);
      throw error;
    }
  }

  /**
   * Actualizar un instrumento/rol existente.
   * PUT /api/v1/worship/roles/{id}
   */
  async updateWorshipRole(id, roleData) {
    try {
      validateId(id, 'roleId');
      if (!roleData || typeof roleData !== 'object') throw new Error('Datos de rol inválidos');
      validateString(roleData.name, 'name', 2, 50);

      log('📝 [updateWorshipRole] Actualizando rol ID:', id);
      const response = await this.request(`/worship/roles/${id}`, {
        method: 'PUT',
        body: JSON.stringify(roleData)
      });
      log('✅ [updateWorshipRole] Éxito');
      return response;
    } catch (error) {
      logError('❌ [updateWorshipRole] Error:', error.message);
      throw error;
    }
  }

  /**
   * Eliminar o desactivar un instrumento/rol.
   * DELETE /api/v1/worship/roles/{id}
   */
  async deleteWorshipRole(id) {
    try {
      validateId(id, 'roleId');
      log('🗑️ [deleteWorshipRole] Eliminando rol ID:', id);
      const response = await this.request(`/worship/roles/${id}`, {
        method: 'DELETE'
      });
      log('✅ [deleteWorshipRole] Éxito');
      return response;
    } catch (error) {
      logError('❌ [deleteWorshipRole] Error:', error.message);
      throw error;
    }
  }

  // ==========================================
  // REPERTORIO (SONGS Y SETLIST) - AÑADIDOS HOY
  // ==========================================
  async getWorshipSongs() {
    // Corregido para evitar el No mapping for GET /api/v1/worship/schedule/songs
    return await this.request('/worship/schedule/songs');
  }
  async createWorshipSong(songData) { return await this.request('/worship/schedule/songs', { method: 'POST', body: JSON.stringify(songData) }); }
  async updateWorshipSong(id, songData) { return await this.request(`/worship/schedule/songs/${id}`, { method: 'PUT', body: JSON.stringify(songData) }); }
  async deleteWorshipSong(id) { return await this.request(`/worship/schedule/songs/${id}`, { method: 'DELETE' }); }
  async syncEventSetlist(eventId, songIds) {
    return await this.request(`/worship/schedule/event/${eventId}/setlist`, {
      method: 'PUT',
      body: JSON.stringify(toArray(songIds).map(id => parseInt(id)).filter(id => !isNaN(id) && id > 0))// Asegurar tipos numéricos
    });
  }


  // Agrega esto justo ANTES de  } // Fin de la clase ApiService
  async getWorshipEventById(id) {
    try {
      validateId(id, 'eventId');
      log('🔍 [getWorshipEventById] Obteniendo evento ID:', id);
      const response = await this.request(`/worship/schedule/event/${id}`);
      log('✅ [getWorshipEventById] Éxito');
      return response;
    } catch (error) {
      logError('❌ [getWorshipEventById] Error:', error.message);
      throw error;
    }
  }

  // Dentro de la clase ApiService en apiService.js

  // En apiService.js, asegúrate de tener este método (ya lo tienes al final):

  async getGlobalSummaryByDate(date) {
    try {
      validateString(date, 'date', 10, 10);
      log('🌐 [getGlobalSummaryByDate] Obteniendo resumen masivo para:', date);
      return await this.request(`/attendance-cell-group/summary/all-cells/date/${date}`);
    } catch (error) {
      logError('❌ [getGlobalSummaryByDate] Error:', error.message);
      throw error;
    }
  }

  // ============================================================
  // 🏛️ MÓDULO DE MINISTERIOS
  // Base URL del módulo: /api/v1/ministeries
  // ============================================================

  /**
   * Listar todos los miembros asignados a equipos ministeriales (DTO)
   */
  async getMinisteryTeams() {
    try {
      log('🏛️ [getMinisteryTeams] Obteniendo equipos ministeriales');
      return await this.request('/ministeries/teams');
    } catch (error) {
      logError('❌ [getMinisteryTeams] Error:', error.message);
      throw error;
    }
  }

  /**
   * Crear un nuevo ministerio
   */
  async createMinistery(name) {
    try {
      validateString(name, 'name', 2, 100);
      const params = new URLSearchParams();
      params.append('name', name.trim());

      log('🏛️ [createMinistery] Creando ministerio:', name);
      return await this.request(`/ministeries?${params.toString()}`, { method: 'POST' });
    } catch (error) {
      logError('❌ [createMinistery] Error:', error.message);
      throw error;
    }
  }

  /**
   * Añadir un rol a un ministerio
   */
  async addRoleToMinistery(ministeryId, roleData) {
    try {
      validateId(ministeryId, 'ministeryId');
      if (!roleData || typeof roleData !== 'object') throw new Error('Datos de rol inválidos');
      validateString(roleData.name, 'name', 2, 100);

      log('🏛️ [addRoleToMinistery] Añadiendo rol al ministerio ID:', ministeryId);
      return await this.request(`/ministeries/${ministeryId}/roles`, {
        method: 'POST',
        body: JSON.stringify(roleData)
      });
    } catch (error) {
      logError('❌ [addRoleToMinistery] Error:', error.message);
      throw error;
    }
  }

  /**
   * Asignar un líder a un equipo ministerial
   */
  async assignLeaderToMinisteryTeam(ministeryId, leaderId, level, roleIds = []) {
    try {
      validateId(ministeryId, 'ministeryId');
      validateId(leaderId, 'leaderId');
      validateString(level, 'level', 1, 20); // LEVEL_1, LEVEL_2, LEVEL_3

      const params = new URLSearchParams();
      params.append('leaderId', leaderId);
      params.append('level', level);
      // Spring Boot espera los arrays repetidos o separados por coma
      roleIds.forEach(id => params.append('roleIds', id));

      log('🏛️ [assignLeaderToMinisteryTeam] Asignando líder al ministerio ID:', ministeryId);
      return await this.request(`/ministeries/${ministeryId}/team?${params.toString()}`, { method: 'POST' });
    } catch (error) {
      logError('❌ [assignLeaderToMinisteryTeam] Error:', error.message);
      throw error;
    }
  }

  // 🏛️ Catálogo de Ministerios
  async getMinisteries() {
    try {
      log('🏛️ [getMinisteries] Obteniendo catálogo de ministerios');
      return await this.request('/ministeries');
    } catch (error) {
      logError('❌ [getMinisteries] Error:', error.message);
      throw error;
    }
  }

  // --- Añadir al bloque de MINISTERIOS en apiService.js ---

  async updateMinistery(id, name, active) {
    try {
      validateId(id, 'ministeryId');
      const params = new URLSearchParams();
      params.append('name', name.trim());

      // Añadir el estado a la URL si viene definido
      if (active !== undefined && active !== null) {
        params.append('active', active);
      }

      return await this.request(`/ministeries/${id}?${params.toString()}`, { method: 'PUT' });
    } catch (error) {
      logError('❌ [updateMinistery] Error:', error.message);
      throw error;
    }
  }

  async deleteMinistery(id) {
    try {
      validateId(id, 'ministeryId');
      return await this.request(`/ministeries/${id}`, { method: 'DELETE' });
    } catch (error) {
      logError('❌ [deleteMinistery] Error:', error.message);
      throw error;
    }
  }

  async updateMinisteryRole(roleId, roleData) {
    try {
      validateId(roleId, 'roleId');
      if (!roleData || typeof roleData !== 'object') throw new Error('Datos de rol inválidos');
      validateString(roleData.name, 'name', 2, 100);

      log('🏛️ [updateMinisteryRole] Actualizando rol ID:', roleId);
      return await this.request(`/ministeries/roles/${roleId}`, {
        method: 'PUT',
        body: JSON.stringify(roleData)
      });
    } catch (error) {
      logError('❌ [updateMinisteryRole] Error:', error.message);
      throw error;
    }
  }

  // Actualizar roles y nivel de un líder ya asignado a un equipo
  async updateMinisteryTeam(teamId, level, roleIds = []) {
    try {
      validateId(teamId, 'teamId');
      validateString(level, 'level', 1, 20);

      const params = new URLSearchParams();
      params.append('level', level);
      roleIds.forEach(id => params.append('roleIds', id));

      log('🏛️ [updateMinisteryTeam] Actualizando equipo ID:', teamId);
      return await this.request(`/ministeries/teams/${teamId}?${params.toString()}`, { method: 'PUT' });
    } catch (error) {
      logError('❌ [updateMinisteryTeam] Error:', error.message);
      throw error;
    }
  }

  async deleteMinisteryRole(roleId) {
    try {
      validateId(roleId, 'roleId');
      return await this.request(`/ministeries/roles/${roleId}`, { method: 'DELETE' });
    } catch (error) {
      logError('❌ [deleteMinisteryRole] Error:', error.message);
      throw error;
    }
  }

  /**
   * Cambiar el estado de un miembro en el equipo ministerial
   */
  async updateMinisteryTeamStatus(teamId, status) {
    try {
      validateId(teamId, 'teamId');
      validateString(status, 'status', 1, 20); // ACTIVE, SUSPENDED, INACTIVE

      log('🏛️ [updateMinisteryTeamStatus] Actualizando estado a:', status);
      return await this.request(`/ministeries/teams/${teamId}/status?status=${status}`, { method: 'PUT' });
    } catch (error) {
      logError('❌ [updateMinisteryTeamStatus] Error:', error.message);
      throw error;
    }
  }

  // --- EVENTOS (PROGRAMACIÓN) ---
  async getMinisteryEvents(ministeryId, year, month) {
    let url = `/ministeries/${ministeryId}/events`;
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month) params.append('month', month);
    if (params.toString()) url += `?${params.toString()}`;
    return await this.request(url);
  }

  async deleteMinisteryEvent(eventId) { return await this.request(`/ministeries/events/${eventId}`, { method: 'DELETE' }); }

  async createMinisteryEvent(ministeryId, name, eventDate) {
    const params = new URLSearchParams();
    params.append('name', name);

    // 🛠️ FIX: Spring Boot requiere los segundos. 
    // Si el string viene como "2026-04-20T19:00" (16 caracteres), le agregamos ":00"
    let safeDate = eventDate;
    if (safeDate.length === 16) {
      safeDate += ":00";
    }

    params.append('eventDate', safeDate);
    return await this.request(`/ministeries/${ministeryId}/events?${params.toString()}`, { method: 'POST' });
  }



  async syncMinisteryEventAssignments(eventId, assignmentsArray) {
    // assignmentsArray = [{ roleId: 1, teamId: 2 }, ...]
    return await this.request(`/ministeries/events/${eventId}/assignments`, {
      method: 'PUT',
      body: JSON.stringify(assignmentsArray)
    });
  }

  // Autogenerar programación mensual (Miércoles y Domingos)
  async autoGenerateMinisterySchedule() {
    try {
      log('⚙️ [autoGenerateMinisterySchedule] Lanzando algoritmo de generación');
      return await this.request('/ministeries/schedule/auto-generate', { method: 'POST' });
    } catch (error) {
      logError('❌ [autoGenerateMinisterySchedule] Error:', error.message);
      throw error;
    }
  }

  // Marcar la asistencia de un miembro en un evento
  async markMinisteryEventAttendance(assignmentId, attended) {
    try {
      return await this.request(`/ministeries/events/assignments/${assignmentId}/attendance?attended=${attended}`, { method: 'PATCH' });
    } catch (error) {
      logError('❌ [markMinisteryEventAttendance] Error:', error.message);
      throw error;
    }
  }

  // Obtener la configuración recurrente de horarios
  async getMinistryScheduleConfig(ministeryId) {
    try {
      validateId(ministeryId, 'ministeryId');
      log('🏛️ [getMinistryScheduleConfig] Obteniendo horarios del ministerio:', ministeryId);
      return await this.request(`/ministeries/${ministeryId}/schedule/config`);
    } catch (error) {
      logError('❌ [getMinistryScheduleConfig] Error:', error.message);
      throw error;
    }
  }

  // Guardar la configuración recurrente de horarios
  async saveMinistryScheduleConfig(ministeryId, configs) {
    try {
      validateId(ministeryId, 'ministeryId');
      log('🏛️ [saveMinistryScheduleConfig] Guardando horarios del ministerio:', ministeryId);
      return await this.request(`/ministeries/${ministeryId}/schedule/config`, {
        method: 'PUT',
        body: JSON.stringify({ configs })
      });
    } catch (error) {
      logError('❌ [saveMinistryScheduleConfig] Error:', error.message);
      throw error;
    }
  }

  // ----------------------------------------------------
  // ORQUESTADOR DE EJECUCIÓN (ROUTING)
  // ----------------------------------------------------
  async executeScheduleAction(ministeryId, payload) {
    const { executionMode } = payload;
    
    // El payload que viaja al backend no necesita el executionMode (ya está implícito en la URL)
    // Pero lo mandaremos igual por si el request body lo requiere.
    if (executionMode === "SIMULATION_VARIANTS") {
      return await this.simulateVariantsSchedule(ministeryId, payload);
    } else if (executionMode === "SIMULATION") {
      return await this.simulateSchedule(ministeryId, payload);
    } else {
      // LIVE (o fallback)
      return await this.generateCustomSchedule(ministeryId, payload);
    }
  }

  // Generación mensual (LIVE)
  async generateCustomSchedule(ministeryId, generationData) {
    try {
      validateId(ministeryId, 'ministeryId');
      log('🏛️ [generateCustomSchedule] Generando programación para ministerio (LIVE):', ministeryId);
      return await this.request(`/ministeries/${ministeryId}/schedule/generate`, {
        method: 'POST',
        body: JSON.stringify(generationData)
      });
    } catch (error) {
      logError('❌ [generateCustomSchedule] Error:', error.message);
      throw error;
    }
  }

  // Simulación Básica (SIMULATION)
  async simulateSchedule(ministeryId, generationData) {
    try {
      validateId(ministeryId, 'ministeryId');
      log('🏛️ [simulateSchedule] Simulando programación (Sin Variantes) para:', ministeryId);
      return await this.request(`/ministeries/${ministeryId}/schedule/simulate`, {
        method: 'POST',
        body: JSON.stringify(generationData)
      });
    } catch (error) {
      logError('❌ [simulateSchedule] Error:', error.message);
      throw error;
    }
  }

  // Simulación Avanzada con Variantes (SIMULATION_VARIANTS)
  async simulateVariantsSchedule(ministeryId, generationData) {
    try {
      validateId(ministeryId, 'ministeryId');
      log('🏛️ [simulateVariantsSchedule] Simulando programación con Variantes para:', ministeryId);
      return await this.request(`/ministeries/${ministeryId}/schedule/simulate-variants`, {
        method: 'POST',
        body: JSON.stringify(generationData)
      });
    } catch (error) {
      logError('❌ [simulateVariantsSchedule] Error:', error.message);
      throw error;
    }
  }

  // Descargar PDF de programación
  async downloadMinistrySchedulePdf(ministeryId, year, month) {
    try {
      validateId(ministeryId, 'ministeryId');
      log('🏛️ [downloadMinistrySchedulePdf] Descargando PDF de programación para ministerio:', ministeryId);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/ministeries/${ministeryId}/schedule/pdf?year=${year}&month=${month}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('No se pudo descargar el PDF de programación');
      }
      return await response.blob();
    } catch (error) {
      logError('❌ [downloadMinistrySchedulePdf] Error:', error.message);
      throw error;
    }
  }

  // Publicar programación mensual y notificar por Telegram
  async publishMinistrySchedule(ministeryId, year, month) {
    try {
      validateId(ministeryId, 'ministeryId');
      log('📢 [publishMinistrySchedule] Publicando programación para ministerio:', ministeryId, year, month);
      return await this.request(
        `/ministeries/${ministeryId}/schedule/publish?year=${year}&month=${month}`,
        { method: 'POST' }
      );
    } catch (error) {
      logError('❌ [publishMinistrySchedule] Error:', error.message);
      throw error;
    }
  }

  // Obtener estadísticas del ministerio
  async getMinistryStatistics(ministeryId) {
    try {
      validateId(ministeryId, 'ministeryId');
      log('🏛️ [getMinistryStatistics] Obteniendo estadísticas del ministerio:', ministeryId);
      return await this.request(`/ministeries/${ministeryId}/statistics`);
    } catch (error) {
      logError('❌ [getMinistryStatistics] Error:', error.message);
      throw error;
    }
  }

  // ============================================================
  // ⭐ MÓDULO EVALUACIÓN DE EXCELENCIA MINISTERIAL
  // ============================================================

  /**
   * Registra las asistencias y evaluaciones de todos los servidores de un culto.
   * @param {number} eventId - ID del MinistryEvent
   * @param {Array} records - Lista de MinistryAttendanceRequest
   */
  async saveMinistryEventAttendance(eventId, records) {
    try {
      validateId(eventId, 'eventId');
      log('📋 [saveMinistryEventAttendance] Guardando asistencias del evento:', eventId);
      return await this.request(
        `/ministeries/events/${eventId}/attendance`,
        {
          method: 'POST',
          body: JSON.stringify(records),
        },
        { 'Content-Type': 'application/json' }
      );
    } catch (error) {
      logError('❌ [saveMinistryEventAttendance] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtiene el resumen de asistencias/evaluaciones de un culto.
   * @param {number} eventId - ID del MinistryEvent
   */
  async getMinistryEventAttendance(eventId) {
    try {
      validateId(eventId, 'eventId');
      log('📋 [getMinistryEventAttendance] Obteniendo asistencias del evento:', eventId);
      return await this.request(`/ministeries/events/${eventId}/attendance`);
    } catch (error) {
      logError('❌ [getMinistryEventAttendance] Error:', error.message);
      throw error;
    }
  }

  /**
   * Dashboard de excelencia de todos los servidores de un ministerio.
   * Ordenado por promedio ascendente (los de mayor riesgo primero).
   * @param {number} ministryId - ID del ministerio
   */
  async getMinistryExcellenceDashboard(ministryId) {
    try {
      validateId(ministryId, 'ministryId');
      log('⭐ [getMinistryExcellenceDashboard] Obteniendo dashboard:', ministryId);
      return await this.request(`/ministeries/${ministryId}/excellence`);
    } catch (error) {
      logError('❌ [getMinistryExcellenceDashboard] Error:', error.message);
      throw error;
    }
  }

  /**
   * Perfil de excelencia individual de un servidor.
   * @param {number} teamId - ID del MinistryTeam
   */
  async getMinistryMemberExcellence(teamId) {
    try {
      validateId(teamId, 'teamId');
      log('⭐ [getMinistryMemberExcellence] Obteniendo perfil:', teamId);
      return await this.request(`/ministeries/teams/${teamId}/excellence`);
    } catch (error) {
      logError('❌ [getMinistryMemberExcellence] Error:', error.message);
      throw error;
    }
  }

  /**
   * Obtiene las últimas 3 evaluaciones detalladas de un servidor.
   * @param {number} teamId - ID del MinistryTeam
   */
  async getMinistryMemberEvaluations(teamId) {
    try {
      validateId(teamId, 'teamId');
      log('⭐ [getMinistryMemberEvaluations] Obteniendo evaluaciones del servidor:', teamId);
      return await this.request(`/ministeries/teams/${teamId}/evaluations`);
    } catch (error) {
      logError('❌ [getMinistryMemberEvaluations] Error:', error.message);
      throw error;
    }
  }

  // ============================================================
  // 📊 MÓDULO COHORT PROGRESS (G12)
  // ============================================================

  async generateCohortProgressReport(payload) {
    try {
      log("📊 [generateCohortProgressReport] Generando reporte");
      const response = await this.request("/cohort-progress/report", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      log("✅ [generateCohortProgressReport] Éxito");
      return response;
    } catch (error) {
      logError("❌ [generateCohortProgressReport] Error:", error.message);
      throw error;
    }
  }

  // ============================================================
  // 🌐 MÓDULO G12 — Health & Configuración
  // Base URL: /actuator/health/g12
  // ============================================================

  /**
   * Verifica el estado de configuración del sistema G12.
   * GET /actuator/health/g12
   *
   * Respuesta OK:
   *   { status: "UP", details: { pastorId: number, pastoraId: number } }
   *
   * Respuesta ERROR:
   *   {
   *     status: "DOWN",
   *     details: { error: string, invalidField?: string, value?: number,
   *                reason?: string, hint?: string }
   *   }
   */
  async getG12Health() {
    try {
      log("🌐 [getG12Health] Consultando health del módulo G12");

      const actuatorUrl = `${API_BASE_URL.replace(/\/api\/v1\/?$/, "")}/actuator/health/g12`;
      const token = sessionStorage.getItem("token");

      const res = await fetch(actuatorUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (res.status === 401) {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
        window.dispatchEvent(new CustomEvent("authTokenExpired"));
        throw new Error("Sesión expirada");
      }

      // Spring Boot Actuator devuelve JSON incluso con 503 (DOWN).
      // Intentamos parsear siempre antes de tirar.
      let body = null;
      try {
        body = await res.json();
      } catch (e) {
        // No es JSON (raro en Actuator)
      }

      if (body && typeof body === "object") {
        log("✅ [getG12Health] status:", body?.status, "| http:", res.status);
        return body;
      }

      // No hubo body parseable → tirar con el status HTTP
      throw new Error(`Error ${res.status}`);
    } catch (error) {
      logError("❌ [getG12Health] Error:", error.message);
      throw error;
    }
  }

  // ============================================================
  // CITACIONES (MEETINGS)
  // ============================================================

  async getMeetings(filters = {}) {
    try {
      log('📅 [getMeetings] Listando citaciones con filtros:', filters);
      let url = '/meetings';
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.status) params.append('status', filters.status);
      if (filters.convocationType) params.append('convocationType', filters.convocationType);
      
      const paramStr = params.toString();
      if (paramStr) {
        url += `?${paramStr}`;
      }
      return await this.request(url);
    } catch (error) {
      logError('❌ [getMeetings] Error:', error.message);
      throw error;
    }
  }

  async getMeeting(id) {
    try {
      validateId(id, 'meetingId');
      log('📅 [getMeeting] Detalle de citación ID:', id);
      return await this.request(`/meetings/${id}`);
    } catch (error) {
      logError('❌ [getMeeting] Error:', error.message);
      throw error;
    }
  }

  async createMeeting(data) {
    try {
      log('📅 [createMeeting] Creando citación:', data.subject);
      return await this.request('/meetings', {
        method: 'POST',
        body: JSON.stringify(data)
      }, { 'Content-Type': 'application/json' });
    } catch (error) {
      logError('❌ [createMeeting] Error:', error.message);
      throw error;
    }
  }
  async cancelMeeting(id, data) {
    try {
      validateId(id, 'meetingId');
      log('📅 [cancelMeeting] Cancelando citación ID:', id, '| Razón:', data.reason);
      return await this.request(`/meetings/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify(data)
      }, { 'Content-Type': 'application/json' });
    } catch (error) {
      logError('❌ [cancelMeeting] Error:', error.message);
      throw error;
    }
  }

  async deleteMeeting(id) {
    try {
      validateId(id, 'meetingId');
      log('📅 [deleteMeeting] Eliminando citación ID:', id);
      return await this.request(`/meetings/${id}`, {
        method: 'DELETE'
      });
    } catch (error) {
      logError('❌ [deleteMeeting] Error:', error.message);
      throw error;
    }
  }

  async rescheduleMeeting(id, data) {
    try {
      validateId(id, 'meetingId');
      log('📅 [rescheduleMeeting] Reprogramando citación ID:', id, '| Nueva Fecha:', data.newDate);
      return await this.request(`/meetings/${id}/reschedule`, {
        method: 'POST',
        body: JSON.stringify(data)
      }, { 'Content-Type': 'application/json' });
    } catch (error) {
      logError('❌ [rescheduleMeeting] Error:', error.message);
      throw error;
    }
  }

  async finalizeMeeting(id) {
    try {
      validateId(id, 'meetingId');
      log('📅 [finalizeMeeting] Finalizando citación ID:', id);
      return await this.request(`/meetings/${id}/finalize`, {
        method: 'POST'
      });
    } catch (error) {
      logError('❌ [finalizeMeeting] Error:', error.message);
      throw error;
    }
  }

  async updateMeetingAttendance(attendeeId, data) {
    try {
      validateId(attendeeId, 'attendeeId');
      log('📅 [updateMeetingAttendance] Actualizando asistencia attendee:', attendeeId, '| Status:', data.status);
      return await this.request(`/meetings/attendance/${attendeeId}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      }, { 'Content-Type': 'application/json' });
    } catch (error) {
      logError('❌ [updateMeetingAttendance] Error:', error.message);
      throw error;
    }
  }

  async bulkUpdateMeetingAttendance(meetingId, items) {
    try {
      validateId(meetingId, 'meetingId');
      log('📅 [bulkUpdateMeetingAttendance] Actualizando asistencia lote para citación ID:', meetingId, '| Items:', items.length);
      return await this.request(`/meetings/${meetingId}/attendance/bulk`, {
        method: 'POST',
        body: JSON.stringify(items)
      }, { 'Content-Type': 'application/json' });
    } catch (error) {
      logError('❌ [bulkUpdateMeetingAttendance] Error:', error.message);
      throw error;
    }
  }

  async checkMeetingConflicts(data) {
    try {
      log('📅 [checkMeetingConflicts] Verificando conflictos preventivos para:', data.subject);
      return await this.request('/meetings/conflicts', {
        method: 'POST',
        body: JSON.stringify(data)
      }, { 'Content-Type': 'application/json' });
    } catch (error) {
      logError('❌ [checkMeetingConflicts] Error:', error.message);
      throw error;
    }
  }

  async getMeetingDashboard(filters = {}) {
    try {
      log('📅 [getMeetingDashboard] Consultando dashboard con filtros:', filters);
      let url = '/meetings/dashboard';
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      
      const paramStr = params.toString();
      if (paramStr) {
        url += `?${paramStr}`;
      }
      return await this.request(url);
    } catch (error) {
      logError('❌ [getMeetingDashboard] Error:', error.message);
      throw error;
    }
  }

  async getMeetingRanking(filters = {}, page = 0, size = 10) {
    try {
      log('📅 [getMeetingRanking] Consultando ranking con filtros:', filters, 'paginación:', { page, size });
      let url = '/meetings/ranking';
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      params.append('page', page);
      params.append('size', size);
      
      const paramStr = params.toString();
      if (paramStr) {
        url += `?${paramStr}`;
      }
      return await this.request(url);
    } catch (error) {
      logError('❌ [getMeetingRanking] Error:', error.message);
      throw error;
    }
  }

} // Fin de la clase ApiService

const apiService = new ApiService();
export default apiService;